


// src/TournamentsSection.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";

import { db } from "../firebase"; 
import { getFunctions, httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { FaChevronRight, FaInfoCircle, FaLock, FaRegFileAlt, FaRegStar, FaStar } from "react-icons/fa";
import { useLanguage } from "../i18n/LanguageProvider";
import GameSelector from "./GameSelector";
import { GameService } from "../services/gameService";
import { CoinIcon, CommunityIcon, CrownIcon, EveningCupIcon, NightlyCupIcon, SpecialCreatorCupIcon } from "./icons";
import CompetitionRulesView from "./CompetitionRulesView";
import PrivateTournamentCodeModal from "./PrivateTournamentCodeModal";
import { buildTournamentRulesViewModel } from "../utils/competitionRules";
import {
  getTournamentFinishedAtMillis,
  isCurrentTournamentParticipant,
  isReachableParticipantTournament,
  isRecentlyFinishedTournament,
  isRunningTournament,
  isUpcomingCurrentParticipantTournament,
  isUpcomingParticipantTournament,
} from "../utils/tournamentVisibility";
import { normalizeTournamentPrizes } from "../utils/tournamentPrizes";
import {
  getTournamentPrizeCardState,
} from "../utils/prizeFulfillmentUi";
import { getTournamentStatusKeyForCard } from "../utils/tournamentStatusPresentation";

export { getTournamentStatusKeyForCard } from "../utils/tournamentStatusPresentation";

export const tournamentStatusDotBaseStyle = {
  width: 12,
  height: 12,
  minWidth: 12,
  borderRadius: 999,
  display: "inline-block",
  flex: "0 0 auto",
  boxShadow: "0 0 10px currentColor",
};

const tournamentStatusVisualStyles = {
  open: {
    border: "2px solid rgba(0,255,120,0.8)",
    color: "rgba(0,255,120,1)",
  },
  running: {
    border: "2px solid rgba(52, 152, 219, 0.95)",
    color: "rgba(52, 152, 219, 1)",
  },
  finished: {
    border: "2px solid rgba(241, 196, 15, 0.95)",
    color: "rgba(241, 196, 15, 1)",
  },
  didNotStart: {
    border: "2px solid rgba(200, 200, 200, 0.75)",
    color: "rgba(220, 220, 220, 0.95)",
  },
  closed: {
    border: "2px solid rgba(255,60,60,0.85)",
    color: "rgba(255,60,60,1)",
  },
  invalid: {
    color: "rgba(0,0,0,1)",
    dotBorder: "1px solid rgba(255,255,255,0.58)",
    dotBoxShadow: "0 0 0 1px rgba(255,255,255,0.18), 0 0 10px rgba(255,255,255,0.18)",
  },
};

export function getTournamentStatusVisualDefinition(statusKey = "open", language = "de") {
  const labels = language === "de"
    ? {
        open: "OFFEN",
        running: "LÄUFT",
        finished: "BEENDET",
        didNotStart: "ENTFÄLLT",
        closed: "VOLL",
        invalid: "UNGÜLTIG",
      }
    : {
        open: "OPEN",
        running: "LIVE",
        finished: "ENDED",
        didNotStart: "CANCELLED",
        closed: "FULL",
        invalid: "INVALID",
      };
  const ariaLabels = language === "de"
    ? {
        open: "Offen",
        running: "Läuft",
        finished: "Beendet",
        didNotStart: "Entfällt",
        closed: "Voll",
        invalid: "Ungültig",
      }
    : {
        open: "Open",
        running: "Live",
        finished: "Ended",
        didNotStart: "Cancelled",
        closed: "Full",
        invalid: "Invalid",
      };
  const normalizedKey = tournamentStatusVisualStyles[statusKey] ? statusKey : "open";

  return {
    label: labels[normalizedKey],
    ariaLabel: ariaLabels[normalizedKey],
    style: tournamentStatusVisualStyles[normalizedKey],
  };
}

export default function TournamentsSection({
  tournaments,
  currentUid,
  joinTournament,
  setSelectedTournamentId,
  setView,
  selectedDayKey,
  setSelectedDayKey,
  onRequestLeave,
  onOpenTournamentDetail,
  hasAdminAccess = false,
  canJoinGames = true,
  onCompletePlayerProfile = null,
  onOpenMyPrizes = null,
}) {
  const { t, language } = useLanguage();

    const [joinModalOpen, setJoinModalOpen] = useState(false);

const [selectedJoinTournament, setSelectedJoinTournament] = useState(null);
const [joinSuccessOpen, setJoinSuccessOpen] = useState(false);
const [joinedTournament, setJoinedTournament] = useState(null);
const [joiningTournament, setJoiningTournament] = useState(false);
const [dailyPrizes, setDailyPrizes] = useState({});
const [calendarDailyPrizesByKey, setCalendarDailyPrizesByKey] = useState({});
const [joinsByDocId, setJoinsByDocId] = useState({});
const [joinedTournamentDocsById, setJoinedTournamentDocsById] = useState({});
const [favoriteTournamentRefsById, setFavoriteTournamentRefsById] = useState({});
const [favoriteTournamentDocsById, setFavoriteTournamentDocsById] = useState({});
const optimisticJoinedDocIdsRef = useRef({});
const favoriteCleanupAttemptedRef = useRef(new Set());
const joinSuccessScreenRef = useRef(null);
const [activeTournamentIds, setActiveTournamentIds] = useState({});
const [winnerNamesByUid, setWinnerNamesByUid] = useState({});
const [nowTick, setNowTick] = useState(Date.now());
const calendarHapticSlotRef = useRef("");
const calendarHapticFrameRef = useRef(null);
const optimisticJoinedDocsRef = useRef({});
const functions = getFunctions();
const joinTournamentSecure = httpsCallable(functions, "joinTournamentSecure");
const unlockPrivateTournamentByCodeSecure = httpsCallable(functions, "unlockPrivateTournamentByCodeSecure");
const listMyPrizeAwardsSecure = useMemo(() => httpsCallable(functions, "listMyPrizeAwardsSecure"), [functions]);
const [infoModalOpen, setInfoModalOpen] = useState(false);
const [infoModalTitle, setInfoModalTitle] = useState("");
const [infoModalText, setInfoModalText] = useState("");
const [infoModalProfileAction, setInfoModalProfileAction] = useState(false);
const [rulesPreviewTournament, setRulesPreviewTournament] = useState(null);
const [rulesPreviewTermsOpen, setRulesPreviewTermsOpen] = useState(false);
const [prizeModalTournament, setPrizeModalTournament] = useState(null);
const [statusLegendOpen, setStatusLegendOpen] = useState(false);
const [tournamentOverviewTab, setTournamentOverviewTab] = useState("planned");
const [privateJoinModalOpen, setPrivateJoinModalOpen] = useState(false);
const [privateJoinCode, setPrivateJoinCode] = useState("");
const [privateJoinError, setPrivateJoinError] = useState("");
const [privateJoinLoading, setPrivateJoinLoading] = useState(false);
const [myPrizeAwards, setMyPrizeAwards] = useState([]);
const [myPrizeRequests, setMyPrizeRequests] = useState([]);
const gameSelectionStorageKey = currentUid
  ? `vaultquest_selected_games_${currentUid}`
  : "vaultquest_selected_games_guest";
const [selectedGames, setSelectedGames] = useState(() => GameService.getDefaultSelectedGameIds());

useEffect(() => {
  let cancelled = false;
  if (!currentUid) {
    setMyPrizeAwards([]);
    setMyPrizeRequests([]);
    return undefined;
  }

  listMyPrizeAwardsSecure({})
    .then((result) => {
      if (cancelled) return;
      const data = result?.data || {};
      setMyPrizeAwards(Array.isArray(data.awards) ? data.awards : []);
      setMyPrizeRequests(Array.isArray(data.requests) ? data.requests : []);
    })
    .catch(() => {
      if (cancelled) return;
      setMyPrizeAwards([]);
      setMyPrizeRequests([]);
    });

  return () => {
    cancelled = true;
  };
}, [currentUid, listMyPrizeAwardsSecure]);

const scrollTournamentJoinConfirmationToTop = useCallback(() => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  if (typeof document !== "undefined") {
    document.documentElement.scrollTop = 0;
    document.documentElement.scrollLeft = 0;
    document.body.scrollTop = 0;
    document.body.scrollLeft = 0;
  }

  const successScreen = joinSuccessScreenRef.current;
  if (!successScreen) return;

  successScreen.scrollTop = 0;
  successScreen.scrollLeft = 0;

  let parent = successScreen.parentElement;
  const body = typeof document !== "undefined" ? document.body : null;
  while (parent && parent !== body) {
    if (
      parent.scrollHeight > parent.clientHeight ||
      parent.scrollWidth > parent.clientWidth
    ) {
      parent.scrollTop = 0;
      parent.scrollLeft = 0;
    }
    parent = parent.parentElement;
  }
}, []);

useEffect(() => {
  if (!joinSuccessOpen || !joinedTournament) return undefined;

  scrollTournamentJoinConfirmationToTop();

  if (
    typeof window === "undefined" ||
    typeof window.requestAnimationFrame !== "function"
  ) {
    return undefined;
  }

  const frame = window.requestAnimationFrame(scrollTournamentJoinConfirmationToTop);
  return () => window.cancelAnimationFrame(frame);
}, [joinSuccessOpen, joinedTournament, scrollTournamentJoinConfirmationToTop]);


const formatDDMM = (date) => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.`;
};

const today = new Date();
today.setHours(0, 0, 0, 0);

// Fallback, falls mal undefined reinkommt


const toKey = (date) => {


  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const monthLabels = {
  de: [
    "JANUAR",
    "FEBRUAR",
    "MÄRZ",
    "APRIL",
    "MAI",
    "JUNI",
    "JULI",
    "AUGUST",
    "SEPTEMBER",
    "OKTOBER",
    "NOVEMBER",
    "DEZEMBER",
  ],
  en: [
    "JANUARY",
    "FEBRUARY",
    "MARCH",
    "APRIL",
    "MAY",
    "JUNE",
    "JULY",
    "AUGUST",
    "SEPTEMBER",
    "OCTOBER",
    "NOVEMBER",
    "DECEMBER",
  ],
};

const monthShortLabels = {
  de: ["JAN", "FEB", "MÄR", "APR", "MAI", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DEZ"],
  en: ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"],
};

const weekdayLabels = {
  de: ["SO", "MO", "DI", "MI", "DO", "FR", "SA"],
  en: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
};

const dayOptions = Array.from({ length: 20 }, (_, index) => {
  const date = new Date(today);
  date.setDate(today.getDate() + index);
  date.setHours(0, 0, 0, 0);

  return {
    id:
      index === 0
        ? "today"
        : index === 1
        ? "tomorrow"
        : `day${index}`,
    label:
      index === 0
        ? t("tournaments.days.today")
        : formatDDMM(date),
    key: toKey(date),
    date,
    dayNumber: String(date.getDate()).padStart(2, "0"),
    weekday: (weekdayLabels[language] || weekdayLabels.en)[date.getDay()],
    monthLabel: (monthLabels[language] || monthLabels.en)[date.getMonth()],
    monthShortLabel: (monthShortLabels[language] || monthShortLabels.en)[date.getMonth()],
    year: date.getFullYear(),
    isToday: index === 0,
    startsNewMonth: index > 0 && date.getDate() === 1,
  };
});

const selectedDay =
  dayOptions.find((day) => day.id === selectedDayKey) || dayOptions[0];
const selectedMonthTitle = `${selectedDay.monthLabel} ${selectedDay.year}`;

const triggerCalendarHapticFeedback = useCallback(() => {
  Haptics.impact({ style: ImpactStyle.Medium }).catch(() => {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(18);
    }
  });
}, []);

const handleCalendarScroll = useCallback((event) => {
  const scroller = event.currentTarget;

  if (calendarHapticFrameRef.current) {
    cancelAnimationFrame(calendarHapticFrameRef.current);
  }

  calendarHapticFrameRef.current = requestAnimationFrame(() => {
    const dayCards = Array.from(scroller.querySelectorAll("[data-calendar-day-id]"));
    if (!dayCards.length) return;

    const scrollerLeft = scroller.scrollLeft;
    let leftVisibleDayId = "";
    let leftDistance = Number.POSITIVE_INFINITY;

    dayCards.forEach((card) => {
      const cardLeft = card.offsetLeft;
      const cardRight = cardLeft + card.offsetWidth;
      const isVisible = cardRight > scrollerLeft;
      const distance = Math.abs(cardLeft - scrollerLeft);

      if (isVisible && distance < leftDistance) {
        leftDistance = distance;
        leftVisibleDayId = card.getAttribute("data-calendar-day-id") || "";
      }
    });

    if (!leftVisibleDayId || leftVisibleDayId === calendarHapticSlotRef.current) return;

    calendarHapticSlotRef.current = leftVisibleDayId;
    setSelectedDayKey(leftVisibleDayId);
    triggerCalendarHapticFeedback();
  });
}, [setSelectedDayKey, triggerCalendarHapticFeedback]);

useEffect(() => {
  calendarHapticSlotRef.current = selectedDay.id;
}, [selectedDay.id]);

useEffect(() => (
  () => {
    if (calendarHapticFrameRef.current) {
      cancelAnimationFrame(calendarHapticFrameRef.current);
    }
  }
), []);

const showProfileCompletionNotice = () => {
  setInfoModalTitle(t("profileCompletion.title"));
  setInfoModalText(`${t("profileCompletion.text")}\n\n${t("profileCompletion.path")}`);
  setInfoModalProfileAction(true);
  setInfoModalOpen(true);
};

const closeInfoModal = () => {
  setInfoModalOpen(false);
  setInfoModalProfileAction(false);
};

const formatPrizeLabel = useCallback((prizeConfig) => {
  if (!prizeConfig || typeof prizeConfig !== "object") {
    return t("tournaments.noPrize");
  }

  const provider = String(prizeConfig.provider || "").trim().toLowerCase();
  const value = Number(prizeConfig.value || 0);
  const currency = String(prizeConfig.currency || "EUR").trim().toUpperCase();

  if (!provider || !value) {
    return t("tournaments.noPrize");
  }

  const providerLabels = {
    amazon: {
      de: "Amazon-Gutschein",
      en: "Amazon voucher",
    },
    media_markt: {
      de: "MediaMarkt-Gutschein",
      en: "MediaMarkt voucher",
    },
    saturn: {
      de: "Saturn-Gutschein",
      en: "Saturn voucher",
    },
    zalando: {
      de: "Zalando-Gutschein",
      en: "Zalando voucher",
    },
    psn: {
      de: "PlayStation-Gutschein",
      en: "PlayStation voucher",
    },
    xbox: {
      de: "Xbox-Gutschein",
      en: "Xbox voucher",
    },
    steam: {
      de: "Steam-Gutschein",
      en: "Steam voucher",
    },
    nintendo: {
      de: "Nintendo-Gutschein",
      en: "Nintendo voucher",
    },
  };

  const currencySymbols = {
    EUR: "€",
    USD: "$",
    GBP: "£",
  };

  const currencySymbol = currencySymbols[currency] || currency;
  const providerText = providerLabels[provider]?.[language] || provider;

  if (language === "de") {
    return `${value} ${currencySymbol} ${providerText}`;
  }

  return `${currencySymbol}${value} ${providerText}`;
}, [language, t]);







const selectedKey = selectedDay?.key || toKey(today);
const selectedPrizeKey = selectedKey;
const selectedGameIdSet = useMemo(
  () => new Set(GameService.normalizeSelectedGameIds(selectedGames)),
  [selectedGames]
);
const tournamentMatchesSelectedGames = useCallback(
  (tournament) => selectedGameIdSet.has(GameService.normalizeGameId(tournament?.gameId)),
  [selectedGameIdSet]
);

const isActiveDailyPrizeSpecial = useCallback((special) => {
  if (!special || typeof special !== "object" || Array.isArray(special)) return false;
  if (Object.keys(special).length === 0) return false;
  if (special.enabled === false || special.active === false || special.disabled === true || special.deleted === true) {
    return false;
  }

  const blockedStatuses = new Set([
    "cancelled",
    "canceled",
    "deleted",
    "did_not_start",
    "completed",
    "finished",
    "rejected",
    "void",
    "withdrawn",
  ]);
  const status = String(special.status || "open").toLowerCase();
  return !blockedStatuses.has(status) &&
    !special.deletedAt &&
    !special.tournamentDeletedAt &&
    !special.removedAt;
}, []);

const cupMarkerDays = useMemo(() => {
  const blockedStatuses = new Set([
    "cancelled",
    "canceled",
    "deleted",
    "did_not_start",
    "completed",
    "finished",
    "rejected",
    "void",
    "withdrawn",
  ]);
  const availableDayKeys = new Set(dayOptions.map((day) => day.key));
  const markedDays = {};

  (Array.isArray(tournaments) ? tournaments : []).forEach((tournament) => {
    const type = String(tournament?.type || tournament?.tournamentType || "").toLowerCase();
    const id = String(tournament?.id || tournament?.tournamentId || "").toLowerCase();
    const isCreatorCup = type === "community";
    const isSpecialCup =
      type === "special" ||
      id === "special" ||
      id.endsWith("_special") ||
      tournament?.specialCup === true ||
      tournament?.isSpecialCup === true;

    if (!isCreatorCup && !isSpecialCup) return;
    if (!tournamentMatchesSelectedGames(tournament)) return;

    const dateKey = String(tournament?.dateKey || tournament?.requestedDate || "").trim();
    if (!availableDayKeys.has(dateKey)) return;

    const status = String(tournament?.status || "open").toLowerCase();
    if (blockedStatuses.has(status) || tournament?.deletedAt || tournament?.tournamentDeletedAt) {
      return;
    }

    markedDays[dateKey] = true;
  });

  Object.entries(calendarDailyPrizesByKey || {}).forEach(([dateKey, prizeConfig]) => {
    if (!availableDayKeys.has(dateKey)) return;

    const special = prizeConfig?.special;
    if (!isActiveDailyPrizeSpecial(special)) return;
    if (!tournamentMatchesSelectedGames(special)) return;

    markedDays[dateKey] = true;
  });

  return markedDays;
}, [calendarDailyPrizesByKey, dayOptions, isActiveDailyPrizeSpecial, tournaments, tournamentMatchesSelectedGames]);

useEffect(() => {
  try {
    const stored = JSON.parse(localStorage.getItem(gameSelectionStorageKey) || "null");
    setSelectedGames(GameService.normalizeSelectedGameIds(stored));
  } catch (error) {
    setSelectedGames(GameService.getDefaultSelectedGameIds());
  }
}, [gameSelectionStorageKey]);

const updateSelectedGames = useCallback(
  (nextGames) => {
    const normalized = GameService.normalizeSelectedGameIds(nextGames);
    setSelectedGames(normalized);
    localStorage.setItem(gameSelectionStorageKey, JSON.stringify(normalized));
  },
  [gameSelectionStorageKey]
);





useEffect(() => {
  const intervalId = setInterval(() => setNowTick(Date.now()), 10 * 1000); // alle 10 Sekunden
  return () => clearInterval(intervalId);
}, []);



useEffect(() => {
  const loadDailyPrizes = async () => {
    try {
      if (!selectedPrizeKey) return;

      const ref = doc(db, "dailyPrizes", selectedPrizeKey); // z.B. 2026-02-10
      const snap = await getDoc(ref);

      if (snap.exists()) {
        setDailyPrizes(snap.data() || {});
      } else {
        setDailyPrizes({});
      }
    } catch (e) {
      console.error("Failed to load dailyPrizes:", e?.code || e?.message || "unknown");
      setDailyPrizes({});
    }
  };

  loadDailyPrizes();
}, [selectedPrizeKey]);

const calendarDailyPrizeKeys = dayOptions.map((day) => day.key).join("|");

useEffect(() => {
  let cancelled = false;
  const dateKeys = calendarDailyPrizeKeys.split("|").filter(Boolean);

  const loadCalendarDailyPrizes = async () => {
    try {
      const entries = await Promise.all(
        dateKeys.map(async (dateKey) => {
          const snapshot = await getDoc(doc(db, "dailyPrizes", dateKey));
          return [dateKey, snapshot.exists() ? snapshot.data() || {} : {}];
        })
      );

      if (!cancelled) {
        setCalendarDailyPrizesByKey(Object.fromEntries(entries));
      }
    } catch (error) {
      if (!cancelled) {
        console.error("Failed to load calendar dailyPrizes:", error?.code || error?.message || "unknown");
        setCalendarDailyPrizesByKey({});
      }
    }
  };

  loadCalendarDailyPrizes();

  return () => {
    cancelled = true;
  };
}, [calendarDailyPrizeKeys]);








const fixedTournaments = useMemo(
  () => [
    {
      id: "evening",
      name: "Evening’s Cup",
      time: "18:00",
      maxPlayers: 16,
      entryCoins: 500,
      prize: formatPrizeLabel(dailyPrizes.evening),
      prizes: { first: formatPrizeLabel(dailyPrizes.evening), second: "", third: "" },
      gameId: GameService.normalizeGameId(),
    },
    {
      id: "nightly",
      name: "Nightly’s Cup",
      time: "21:00",
      maxPlayers: 32,
      entryCoins: 500,
      prize: formatPrizeLabel(dailyPrizes.nightly),
      prizes: { first: formatPrizeLabel(dailyPrizes.nightly), second: "", third: "" },
      gameId: GameService.normalizeGameId(),
    },
  ],
  [dailyPrizes.evening, dailyPrizes.nightly, formatPrizeLabel]
);

const tournamentDocsById = useMemo(() => {
  const map = {};
  (Array.isArray(tournaments) ? tournaments : []).forEach((tournament) => {
    const id = String(tournament?.id || "").trim();
    if (!id) return;
    map[id] = tournament;
  });
  return map;
}, [tournaments]);

const selectedSpecialTournamentDoc = tournamentDocsById?.[`${selectedKey}_special`] || null;
const special = useMemo(() => {
  if (isActiveDailyPrizeSpecial(dailyPrizes?.special)) {
    return {
      ...(selectedSpecialTournamentDoc || {}),
      ...dailyPrizes.special,
      id: selectedSpecialTournamentDoc?.id || "special",
      tournamentId: selectedSpecialTournamentDoc?.tournamentId || "special",
      dateKey: selectedSpecialTournamentDoc?.dateKey || selectedKey,
      runtimeTournamentMissing: !selectedSpecialTournamentDoc,
    };
  }
  return selectedSpecialTournamentDoc || {};
}, [dailyPrizes?.special, isActiveDailyPrizeSpecial, selectedKey, selectedSpecialTournamentDoc]);
const shouldShowSpecial = Object.keys(special).length > 0;

const getTournamentDocId = useCallback(
  (tournament) => {
    const joinDocId = String(tournament?.joinDocId || "").trim();
    if (joinDocId) return joinDocId;

    const rawId = String(tournament?.id || "").trim();
    if (/^\d{4}-\d{2}-\d{2}_/.test(rawId)) return rawId;

    const tournamentId = String(tournament?.tournamentId || "").trim();
    if (/^\d{4}-\d{2}-\d{2}_/.test(tournamentId)) return tournamentId;

    if (!rawId && tournamentId) return tournamentId;

    const dateKey = String(
      tournament?.dateKey ||
      tournament?.requestedDate ||
      tournament?.dayKey ||
      selectedKey ||
      ""
    ).trim();

    if (dateKey && ["evening", "nightly", "special"].includes(tournamentId)) {
      return `${dateKey}_${tournamentId}`;
    }

    if (tournamentId) return tournamentId;

    if (!rawId) return "";

    return dateKey ? `${dateKey}_${rawId}` : rawId;
  },
  [selectedKey]
);

const getScheduledTournamentFallback = useCallback((tournamentId) => {
  const match = String(tournamentId || "").match(/^(\d{4}-\d{2}-\d{2})_(evening|nightly|special)$/);
  if (!match) return null;

  const [, dateKey, type] = match;
  const isNightly = type === "nightly";
  const isSpecial = type === "special";

  return {
    id: type,
    joinDocId: tournamentId,
    tournamentId,
    dateKey,
    name: isSpecial ? "Special Cup" : isNightly ? "Nightly’s Cup" : "Evening’s Cup",
    time: isSpecial ? "20:00" : isNightly ? "21:00" : "18:00",
    maxPlayers: isSpecial ? 64 : isNightly ? 32 : 16,
    entryCoins: 500,
    prize: t("tournaments.noPrize"),
    prizes: { first: "", second: "", third: "" },
    type,
    visibility: "public",
    gameId: GameService.normalizeGameId(),
  };
}, [t]);

const isPrivateTournament = useCallback(
  (tournament) => String(tournament?.visibility || "public").toLowerCase() === "private",
  []
);

const hasPrivateAccessForTournament = useCallback(
  (tournament) => {
    if (!isPrivateTournament(tournament)) return true;
    if (!currentUid) return false;

    const privateAccessUids = Array.isArray(tournament?.privateAccessUids)
      ? tournament.privateAccessUids.map((value) => String(value))
      : [];
    const privateUnlockedBy =
      tournament?.privateUnlockedBy && typeof tournament.privateUnlockedBy === "object"
        ? tournament.privateUnlockedBy
        : {};
    const creatorUid = String(tournament?.creatorUid || "");

    return (
      creatorUid === currentUid ||
      privateAccessUids.includes(currentUid) ||
      Object.prototype.hasOwnProperty.call(privateUnlockedBy, currentUid)
    );
  },
  [currentUid, isPrivateTournament]
);

const openPrivateJoinModal = useCallback((tournament) => {
  setSelectedJoinTournament(tournament);
  setPrivateJoinCode("");
  setPrivateJoinError("");
  setPrivateJoinModalOpen(true);
}, []);

const closePrivateJoinModal = useCallback(() => {
  if (privateJoinLoading) return;
  setPrivateJoinModalOpen(false);
  setSelectedJoinTournament(null);
  setPrivateJoinError("");
  setPrivateJoinCode("");
}, [privateJoinLoading]);

const openTournamentDetail = (joinDocId, tournament = null, options = {}) => {
  if (!joinDocId) return;

  if (typeof onOpenTournamentDetail === "function") {
    onOpenTournamentDetail(joinDocId, tournament, options);
    return;
  }

  setSelectedTournamentId(joinDocId);
  setView("DETAIL");
};

const markTournamentJoinedLocally = (joinDocId, tournament = null) => {
  if (!joinDocId || !currentUid) return;

  const sourceDoc = tournament
    ? {
        ...tournament,
        id: joinDocId,
        tournamentId: tournament.tournamentId || tournament.id || joinDocId,
        dateKey: tournament.dateKey || getTournamentDateKey(tournament, joinDocId),
        status: tournament.status || "open",
      }
    : { id: joinDocId, tournamentId: joinDocId, status: "open" };
  const sourcePlayers = Array.isArray(sourceDoc.players) ? sourceDoc.players : [];
  const optimisticDoc = {
    ...sourceDoc,
    players: sourcePlayers.includes(currentUid) ? sourcePlayers : [...sourcePlayers, currentUid],
    joinedAt: {
      ...(sourceDoc.joinedAt || {}),
      [currentUid]: sourceDoc.joinedAt?.[currentUid] || true,
    },
  };

  optimisticJoinedDocIdsRef.current = {
    ...optimisticJoinedDocIdsRef.current,
    [joinDocId]: true,
  };
  optimisticJoinedDocsRef.current = {
    ...optimisticJoinedDocsRef.current,
    [joinDocId]: optimisticDoc,
  };

  setJoinsByDocId((prev) => {
    const prevDoc = { ...sourceDoc, ...(prev?.[joinDocId] || {}) };
    const prevPlayers = Array.isArray(prevDoc.players) ? prevDoc.players : [];

    if (prevPlayers.includes(currentUid)) return prev;

    return {
      ...prev,
      [joinDocId]: {
        ...prevDoc,
        players: [...prevPlayers, currentUid],
        joinedAt: {
          ...(prevDoc.joinedAt || {}),
          [currentUid]: prevDoc.joinedAt?.[currentUid] || true,
        },
      },
    };
  });
};

const communityTournaments = useMemo(() => (
  (Array.isArray(tournaments) ? tournaments : [])
    .filter((tournament) => {
      if (String(tournament?.type || "").toLowerCase() !== "community") return false;
      return String(tournament?.dateKey || tournament?.requestedDate || "") === selectedKey;
    })
    .filter(tournamentMatchesSelectedGames)
    .map((tournament) => ({
      id: tournament.id,
      joinDocId: tournament.id,
      name: tournament.name || tournament.title || "Community Turnier",
      time: tournament.time || tournament.requestedStartTime || "",
      maxPlayers: tournament.maxPlayers || 16,
      entryCoins: tournament.entryCoins || 500,
      prize: tournament.prizeText || tournament.prize || t("tournaments.noPrize"),
      prizePool: tournament.prizePool || "",
      prizes: normalizeTournamentPrizes(tournament),
      groupStageEnabled: tournament.groupStageEnabled === true,
      format: tournament.format,
      groupStageStatus: tournament.groupStageStatus,
      knockoutStatus: tournament.knockoutStatus,
      gameMode: tournament.gameMode,
      matchDuration: tournament.matchDuration,
      teamSelection: tournament.teamSelection,
      teamChoice: tournament.teamChoice,
      platform: tournament.platform,
      rulesPlatform: tournament.rulesPlatform,
      tieDecision: tournament.tieDecision,
      customRules: tournament.customRules || "",
      creatorCustomRules: tournament.creatorCustomRules || "",
      creatorRules: Array.isArray(tournament.creatorRules) ? tournament.creatorRules : [],
      additionalCreatorRules: Array.isArray(tournament.additionalCreatorRules)
        ? tournament.additionalCreatorRules
        : [],
      selectedStandardRules: Array.isArray(tournament.selectedStandardRules)
        ? tournament.selectedStandardRules
        : [],
      additionalRulesType: tournament.additionalRulesType || tournament.rulesOwnerType || "creator",
      type: "community",
      visibility: tournament.visibility || "public",
      creatorUid: tournament.creatorUid || "",
      privateAccessUids: Array.isArray(tournament.privateAccessUids)
        ? tournament.privateAccessUids
        : [],
      privateUnlockedBy: tournament.privateUnlockedBy || {},
      gameId: GameService.normalizeGameId(tournament.gameId),
    }))
    .sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")))
), [selectedKey, tournaments, t, tournamentMatchesSelectedGames]);

// ------------------------------------
// JOIN FIXED TOURNAMENT (Firestore)
// ------------------------------------
const joinFixedTournamentFirestore = async (tournament, options = {}) => {
if (!currentUid) {
  setInfoModalTitle(t("tournaments.modals.notLoggedInTitle"));
setInfoModalText(t("tournaments.modals.notLoggedInText"));
  setInfoModalProfileAction(false);
  setInfoModalOpen(true);
  return false;
}

if (!canJoinGames) {
  showProfileCompletionNotice();
  return false;
}

  // z.B. "2026-02-10_evening"
  const joinDocId = getTournamentDocId(tournament);

  if (tournament?.runtimeTournamentMissing === true) {
    setInfoModalTitle(t("tournaments.modals.joinNotPossibleTitle"));
    setInfoModalText(t("tournaments.modals.notJoinableText"));
    setInfoModalOpen(true);
    return false;
  }

  if (
    !options.skipPrivateAccessCheck &&
    isPrivateTournament(tournament) &&
    !hasPrivateAccessForTournament(tournament)
  ) {
    openPrivateJoinModal(tournament);
    return false;
  }

  // ✅ Join sperren, wenn Turnier schon gestartet ist
const now = new Date();

// t.time ist bei dir z.B. "18:00" / "21:00" / "20:00"
const startTime = tournament?.time;
if (startTime && selectedKey && /^\d{4}-\d{2}-\d{2}$/.test(selectedKey)) {
  const [y, m, d] = selectedKey.split("-").map(Number);
  const [hh, mm] = String(startTime).split(":").map(Number);
  const startAt = new Date(y, m - 1, d, hh, mm, 0, 0);

 if (now.getTime() >= startAt.getTime()) {
  setInfoModalTitle(t("tournaments.modals.joinNotPossibleTitle"));
setInfoModalText(t("tournaments.modals.alreadyStartedText"));
  setInfoModalOpen(true);
  return false;
}

}

  if (joiningTournament) return false;

try {
  setJoiningTournament(true);
  const joinResult = await joinTournamentSecure({ tournamentId: joinDocId });
  const joinData = joinResult?.data || {};
  const membershipConfirmed =
    joinData.success === true &&
    (joinData.joined === true || joinData.alreadyJoined === true || joinData.restored === true);

  if (!membershipConfirmed) {
    console.warn("Join did not confirm tournament membership:", joinData);
    setInfoModalTitle(t("tournaments.modals.joinNotPossibleTitle"));
    setInfoModalText(t("tournaments.modals.joinErrorText"));
    setInfoModalOpen(true);
    return false;
  }

  markTournamentJoinedLocally(joinDocId, tournament);

  return true;
} catch (e) {
  console.error("Join failed:", e?.code || e?.message || "unknown");

  const rawCode = String(e?.code || "");
  const rawMsg = String(e?.message || "");
  const code = rawCode.replace("functions/", "");
  const msg = rawMsg;

  const details = e?.details || {};
  const isBanned =
    msg.includes("BANNED") ||
    details?.reason === "BANNED" ||
    details?.banned === true;

  const isPermanent =
    details?.permanent === true ||
    details?.banType === "permanent";

  const bannedUntilRaw = details?.bannedUntil || null;

  const formatBanDate = (value) => {
    if (!value) return null;

    try {
      if (typeof value?.toDate === "function") {
        const d = value.toDate();
        return d.toLocaleString(language === "de" ? "de-DE" : "en-GB");
      }

      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString(language === "de" ? "de-DE" : "en-GB");
      }

      return null;
    } catch {
      return null;
    }
  };

  const bannedUntilText = formatBanDate(bannedUntilRaw);

  if (isBanned) {
    setInfoModalTitle(t("tournaments.modals.accountBannedTitle"));

    if (isPermanent) {
      setInfoModalText(
        t("tournaments.modals.accountBannedPermanentText")
      );
    } else if (bannedUntilText) {
      setInfoModalText(
  language === "de"
    ? `Dein Account wurde vorübergehend gesperrt. Eine Teilnahme ist bis ${bannedUntilText} nicht möglich.`
    : `Your account has been temporarily suspended. Participation is not possible until ${bannedUntilText}.`
);
    } else {
      setInfoModalText(t("tournaments.modals.accountBannedTemporaryText"));
    }
  }

  else if (msg.includes("Tournament already started")) {
    setInfoModalTitle(t("tournaments.modals.joinNotPossibleTitle"));
setInfoModalText(t("tournaments.modals.alreadyStartedJoinBlockedText"));
  }

  else if (msg.includes("Tournament not open")) {
   setInfoModalTitle(t("tournaments.modals.joinNotPossibleTitle"));
setInfoModalText(t("tournaments.modals.notJoinableText"));
  }

  else if (msg.includes("TOURNAMENT_NOT_FOUND") || msg.includes("Tournament not found")) {
    setInfoModalTitle(t("tournaments.modals.joinNotPossibleTitle"));
    setInfoModalText(t("tournaments.modals.notJoinableText"));
  }

  else if (msg.includes("Tournament full")) {
    setInfoModalTitle(t("tournaments.modals.tournamentFullTitle"));
setInfoModalText(t("tournaments.modals.tournamentFullText"));
  }

  else if (code === "failed-precondition" && msg.includes("Not enough coins")) {
    setInfoModalTitle(t("tournaments.modals.notEnoughCoinsTitle"));
setInfoModalText(t("tournaments.modals.notEnoughCoinsText"));
  }

  else if (code === "permission-denied" && msg.includes("PRIVATE_TOURNAMENT_CODE_REQUIRED")) {
    setInfoModalTitle(t("tournaments.privateJoin.title"));
    setInfoModalText(t("tournaments.privateJoin.requiredText"));
  }

  else if (
    msg.includes("PLAYER_PROFILE_INCOMPLETE") ||
    details?.missingConsoleId === true ||
    details?.missingEaId === true
  ) {
    setInfoModalTitle(t("profileCompletion.title"));
    setInfoModalText(`${t("profileCompletion.text")}\n\n${t("profileCompletion.path")}`);
    setInfoModalProfileAction(true);
  }

  else {
    setInfoModalTitle(t("tournaments.modals.errorTitle"));
setInfoModalText(t("tournaments.modals.joinErrorText"));
  }

  setInfoModalOpen(true);
  return false;
} finally {
  setJoiningTournament(false);
}

};

const submitPrivateJoinCode = async () => {
  if (privateJoinLoading || privateJoinCode.length !== 6 || !selectedJoinTournament) return;

  const selectedTournament = selectedJoinTournament;
  const expectedTournamentId = getTournamentDocId(selectedTournament);

  try {
    setPrivateJoinLoading(true);
    setPrivateJoinError("");

    const result = await unlockPrivateTournamentByCodeSecure({ code: privateJoinCode });
    const unlockedTournamentId = String(result?.data?.tournament?.id || "");

    if (unlockedTournamentId !== expectedTournamentId) {
      setPrivateJoinError(t("tournaments.privateJoin.invalidCode"));
      return;
    }

    const tournamentWithAccess = {
      ...selectedTournament,
      privateAccessUids: [
        ...(Array.isArray(selectedTournament?.privateAccessUids)
          ? selectedTournament.privateAccessUids
          : []),
        currentUid,
      ],
    };

    const ok = await joinFixedTournamentFirestore(tournamentWithAccess, {
      skipPrivateAccessCheck: true,
    });

    if (!ok) {
      setPrivateJoinModalOpen(false);
      setSelectedJoinTournament(null);
      return;
    }

    setPrivateJoinModalOpen(false);
    markTournamentJoinedLocally(expectedTournamentId, tournamentWithAccess);
    setJoinedTournament({
      ...tournamentWithAccess,
      id: expectedTournamentId,
      tournamentId: expectedTournamentId,
      dayKey: selectedKey,
    });
    setJoinSuccessOpen(true);
    setSelectedJoinTournament(null);
  } catch (error) {
    console.error("Private tournament code failed:", error?.code || error?.message || "unknown");
    setPrivateJoinError(t("tournaments.privateJoin.invalidCode"));
  } finally {
    setPrivateJoinLoading(false);
  }
};




const baseTournamentsToShow = useMemo(
  () =>
    shouldShowSpecial
      ? [
          ...fixedTournaments,
          {
            id: "special",
            name: special.name || special.title || "Special Cup",
            time: special.time || special.startTime || special.requestedStartTime || "20:00",
            maxPlayers: special.maxPlayers || 64,
            entryCoins: special.entryCoins || 500,
            prize: special.prizeText ? special.prizeText : formatPrizeLabel(special),
            prizePool: special.prizePool || "",
            prizes: normalizeTournamentPrizes(special),
            groupStageEnabled: special.groupStageEnabled === true,
            format: special.format,
            groupStageStatus: special.groupStageStatus,
            knockoutStatus: special.knockoutStatus,
            gameMode: special.gameMode,
            matchDuration: special.matchDuration,
            teamSelection: special.teamSelection,
            teamChoice: special.teamChoice,
            platform: special.platform,
            rulesPlatform: special.rulesPlatform,
            tieDecision: special.tieDecision,
            customRules: special.customRules || "",
            tournamentCustomRules: special.tournamentCustomRules || "",
            specialCupCustomRules: special.specialCupCustomRules || "",
            tournamentRules: Array.isArray(special.tournamentRules) ? special.tournamentRules : [],
            additionalTournamentRules: Array.isArray(special.additionalTournamentRules)
              ? special.additionalTournamentRules
              : [],
            specialCupRules: Array.isArray(special.specialCupRules) ? special.specialCupRules : [],
            additionalRulesType: special.additionalRulesType || "tournament",
            gameId: GameService.normalizeGameId(special.gameId),
            type: "special",
            visibility: special.visibility || "public",
            creatorUid: special.creatorUid || "",
            privateAccessUids: Array.isArray(special.privateAccessUids)
              ? special.privateAccessUids
              : [],
            privateUnlockedBy: special.privateUnlockedBy || {},
          },
        ]
      : fixedTournaments,
  [shouldShowSpecial, fixedTournaments, special, formatPrizeLabel]
);

const tournamentsToShow = useMemo(
  () =>
    [...baseTournamentsToShow, ...communityTournaments]
      .filter(tournamentMatchesSelectedGames)
      .sort((a, b) => String(a.time || "").localeCompare(String(b.time || ""))),
  [baseTournamentsToShow, communityTournaments, tournamentMatchesSelectedGames]
);

const getTimestampMillis = useCallback((value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}, []);

const isInvalidFavoriteTournament = useCallback((tournament) => {
  if (!tournament) return false;
  if (tournament.deletedAt || tournament.tournamentDeletedAt || tournament.removedAt) return true;

  const status = String(tournament.status || "open").toLowerCase();
  return [
    "cancelled",
    "canceled",
    "did_not_start",
    "not_started",
    "startet_nicht",
    "void",
    "deleted",
    "rejected",
    "withdrawn",
  ].includes(status);
}, []);

const isRecentFinishedFavoriteTournament = useCallback((tournament) => {
  if (!tournament) return false;
  const status = String(tournament.status || "open").toLowerCase();
  const isFinished = status === "finished" || !!tournament.winnerUid;
  if (!isFinished) return false;

  const finishedAtMillis = getTournamentFinishedAtMillis(tournament);
  if (!finishedAtMillis) return true;
  return Date.now() - finishedAtMillis <= 24 * 60 * 60 * 1000;
}, []);

const getTournamentTimeLabel = useCallback((tournament) => {
  const explicitTime =
    tournament?.time ||
    tournament?.startTime ||
    tournament?.requestedStartTime ||
    tournament?.scheduledTime;
  if (explicitTime) return String(explicitTime);

  const startAt = tournament?.startAt || tournament?.scheduledStartAt;
  const startDate = startAt?.toDate?.() || (startAt instanceof Date ? startAt : null);
  if (!startDate) return "";

  return startDate.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}, []);

const getTournamentDateKey = useCallback((tournament, fallbackId = "") => {
  const explicitDateKey =
    tournament?.dateKey ||
    tournament?.dayKey ||
    tournament?.requestedDate ||
    tournament?.scheduledDate;
  if (explicitDateKey) return String(explicitDateKey);

  const match = String(fallbackId || "").match(/^(\d{4}-\d{2}-\d{2})_/);
  if (match) return match[1];

  const startAt = tournament?.startAt || tournament?.scheduledStartAt;
  const startDate = startAt?.toDate?.() || (startAt instanceof Date ? startAt : null);
  return startDate ? toKey(startDate) : "";
}, []);

const getTournamentStartMillis = useCallback((tournament, fallbackId = "") => {
  const direct =
    getTimestampMillis(tournament?.startAt) ||
    getTimestampMillis(tournament?.scheduledStartAt);
  if (direct) return direct;

  const dateKey = getTournamentDateKey(tournament, fallbackId);
  const time = getTournamentTimeLabel(tournament) || "00:00";
  if (!dateKey) return 0;

  const parsed = Date.parse(`${dateKey}T${time}:00`);
  return Number.isFinite(parsed) ? parsed : 0;
}, [getTimestampMillis, getTournamentDateKey, getTournamentTimeLabel]);

const getTournamentStartDisplayInfo = useCallback((tournament, fallbackId = "") => {
  const dateKey = getTournamentDateKey(tournament, fallbackId);
  const time = getTournamentTimeLabel(tournament);
  if (!dateKey || !time) return null;

  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return null;

  const startDay = new Date(year, month - 1, day);
  startDay.setHours(0, 0, 0, 0);

  const todayStart = new Date(nowTick);
  todayStart.setHours(0, 0, 0, 0);

  const dayDiff = Math.round((startDay.getTime() - todayStart.getTime()) / (24 * 60 * 60 * 1000));
  let dateLabel = `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}.`;

  if (dayDiff === 0) {
    dateLabel = t("tournaments.startInfo.today");
  } else if (dayDiff === 1) {
    dateLabel = t("tournaments.startInfo.tomorrow");
  } else if (year !== todayStart.getFullYear()) {
    dateLabel = `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}.${year}`;
  }

  const startMillis = getTournamentStartMillis(tournament, fallbackId);
  const minutesUntilStart = startMillis ? Math.ceil((startMillis - nowTick) / (60 * 1000)) : null;
  let countdown = "";

  if (Number.isFinite(minutesUntilStart) && minutesUntilStart > 0 && minutesUntilStart <= 180) {
    const hours = Math.floor(minutesUntilStart / 60);
    const minutes = minutesUntilStart % 60;
    const parts = [];
    if (hours > 0) parts.push(`${hours} ${t("tournaments.startInfo.hoursShort")}`);
    if (minutes > 0 || hours === 0) parts.push(`${minutes} ${t("tournaments.startInfo.minutesShort")}`);
    countdown = t("tournaments.startInfo.startsIn", { time: parts.join(" ") });
  }

  return {
    label: `${dateLabel} · ${time} ${t("tournaments.common.clock")}`,
    countdown,
    startMillis,
  };
}, [getTournamentDateKey, getTournamentStartMillis, getTournamentTimeLabel, nowTick, t]);

useEffect(() => {
  if (!currentUid) {
    setJoinedTournamentDocsById({});
    return undefined;
  }

  const unsubscribe = onSnapshot(
    query(
      collection(db, "tournaments"),
      where("players", "array-contains", currentUid)
    ),
    (snapshot) => {
      const next = {};
      Object.entries(optimisticJoinedDocsRef.current || {}).forEach(([id, tournament]) => {
        if (isCurrentTournamentParticipant(tournament, currentUid)) {
          next[id] = { id, ...tournament };
        }
      });
      snapshot.docs.forEach((snap) => {
        const tournament = { id: snap.id, ...snap.data() };
        if (!isCurrentTournamentParticipant(tournament, currentUid)) return;
        next[snap.id] = tournament;
      });
      setJoinedTournamentDocsById(next);
    },
    (err) => {
      console.error("Joined tournaments onSnapshot failed:", err?.code || err?.message || "unknown");
      const optimisticOnly = {};
      Object.entries(optimisticJoinedDocsRef.current || {}).forEach(([id, tournament]) => {
        if (isCurrentTournamentParticipant(tournament, currentUid)) {
          optimisticOnly[id] = { id, ...tournament };
        }
      });
      setJoinedTournamentDocsById(optimisticOnly);
    }
  );

  return () => unsubscribe();
}, [currentUid]);

useEffect(() => {
  if (!currentUid) return;

  setJoinedTournamentDocsById((prev) => {
    const next = { ...(prev || {}) };
    let changed = false;
    Object.entries(joinsByDocId || {}).forEach(([id, tournament]) => {
      if (!isCurrentTournamentParticipant(tournament, currentUid)) return;
      next[id] = { ...(next[id] || {}), ...tournament, id };
      changed = true;
    });
    return changed ? next : prev;
  });
}, [currentUid, joinsByDocId]);

useEffect(() => {
  if (!currentUid) {
    setFavoriteTournamentRefsById({});
    setFavoriteTournamentDocsById({});
    return undefined;
  }

  const favoritesRef = collection(db, "users", currentUid, "favoriteTournaments");
  const unsubscribe = onSnapshot(
    favoritesRef,
    (snapshot) => {
      const next = {};
      snapshot.docs.forEach((snap) => {
        const data = snap.data() || {};
        const tournamentId = String(data.tournamentId || snap.id || "").trim();
        if (!tournamentId) return;
        next[tournamentId] = {
          ...data,
          docId: snap.id,
          tournamentId,
        };
      });
      setFavoriteTournamentRefsById(next);
    },
    (err) => {
      console.error("Favorite tournaments onSnapshot failed:", err?.code || err?.message || "unknown");
      setFavoriteTournamentRefsById({});
    }
  );

  return () => unsubscribe();
}, [currentUid]);

useEffect(() => {
  if (!currentUid) {
    setFavoriteTournamentDocsById({});
    return undefined;
  }

  const favoriteRefs = Object.values(favoriteTournamentRefsById || {});
  if (favoriteRefs.length === 0) {
    setFavoriteTournamentDocsById({});
    return undefined;
  }

  setFavoriteTournamentDocsById((prev) => {
    const next = {};
    favoriteRefs.forEach((favorite) => {
      if (prev[favorite.tournamentId]) next[favorite.tournamentId] = prev[favorite.tournamentId];
    });
    return next;
  });

  const unsubs = favoriteRefs.map((favorite) => {
    const tournamentId = favorite.tournamentId;
    return onSnapshot(
      doc(db, "tournaments", tournamentId),
      (snap) => {
        if (!snap.exists()) {
          const fallbackTournament = getScheduledTournamentFallback(tournamentId);
          if (fallbackTournament) {
            setFavoriteTournamentDocsById((prev) => ({
              ...prev,
              [tournamentId]: fallbackTournament,
            }));
            return;
          }

          setFavoriteTournamentDocsById((prev) => {
            const next = { ...prev };
            delete next[tournamentId];
            return next;
          });
          deleteDoc(doc(db, "users", currentUid, "favoriteTournaments", favorite.docId)).catch((err) => {
            console.error("Failed to remove missing favorite tournament:", err?.code || err?.message || "unknown");
          });
          return;
        }

        setFavoriteTournamentDocsById((prev) => ({
          ...prev,
          [tournamentId]: { id: tournamentId, ...snap.data() },
        }));
      },
      (err) => {
        console.error("Favorite tournament doc onSnapshot failed:", err?.code || err?.message || "unknown");
      }
    );
  });

  return () => {
    unsubs.forEach((unsubscribe) => unsubscribe && unsubscribe());
  };
}, [currentUid, favoriteTournamentRefsById, getScheduledTournamentFallback]);

const participantTournamentDocsById = useMemo(() => {
  const next = {};
  (Array.isArray(tournaments) ? tournaments : []).forEach((tournament) => {
    const id = String(getTournamentDocId(tournament) || tournament?.id || "").trim();
    if (!id) return;
    next[id] = { id, ...tournament };
  });
  return { ...next, ...joinedTournamentDocsById };
}, [tournaments, joinedTournamentDocsById, getTournamentDocId]);

const currentParticipantTournamentDocsById = useMemo(() => {
  const next = {};
  Object.entries(participantTournamentDocsById || {}).forEach(([id, tournament]) => {
    if (!isCurrentTournamentParticipant(tournament, currentUid)) return;
    next[id] = { id, ...tournament };
  });
  Object.entries(joinsByDocId || {}).forEach(([id, tournament]) => {
    if (!isCurrentTournamentParticipant(tournament, currentUid)) return;
    next[id] = { id, ...tournament };
  });
  return next;
}, [currentUid, joinsByDocId, participantTournamentDocsById]);

const mapParticipantTournamentToCard = useCallback((joinDocId, tournament) => {
  if (!joinDocId || !tournament) return null;
  const dateKey = getTournamentDateKey(tournament, joinDocId);
  const baseId = String(tournament.tournamentId || joinDocId.split("_").slice(1).join("_") || tournament.id || joinDocId);
  const type = String(tournament.type || baseId || "").toLowerCase();
  const isSpecial = joinDocId.endsWith("_special") || type === "special";
  const name =
    tournament.name ||
    tournament.title ||
    (isSpecial ? "Special Cup" : type === "community" ? "Community Turnier" : baseId === "nightly" ? "Nightly’s Cup" : "Evening’s Cup");

  return {
    id: baseId || joinDocId,
    joinDocId,
    dateKey,
    name,
    time: getTournamentTimeLabel(tournament),
    maxPlayers: tournament.maxPlayers || 0,
    entryCoins: tournament.entryCoins || 500,
    prize: tournament.prizeText || tournament.prize || formatPrizeLabel(tournament) || t("tournaments.noPrize"),
    prizePool: tournament.prizePool || "",
    prizes: normalizeTournamentPrizes(tournament),
    groupStageEnabled: tournament.groupStageEnabled === true,
    format: tournament.format,
    groupStageStatus: tournament.groupStageStatus,
    knockoutStatus: tournament.knockoutStatus,
    type: tournament.type || (isSpecial ? "special" : baseId),
    visibility: tournament.visibility || "public",
    creatorUid: tournament.creatorUid || "",
    privateAccessUids: Array.isArray(tournament.privateAccessUids)
      ? tournament.privateAccessUids
      : [],
    privateUnlockedBy: tournament.privateUnlockedBy || {},
    gameId: GameService.normalizeGameId(tournament.gameId),
  };
}, [formatPrizeLabel, getTournamentDateKey, getTournamentTimeLabel, t]);

const allVisibleTournamentCards = useMemo(() => (
  Object.entries(tournamentDocsById)
    .filter(([, tournament]) => tournamentMatchesSelectedGames(tournament))
    .filter(([, tournament]) => hasPrivateAccessForTournament(tournament) || hasAdminAccess)
    .map(([joinDocId, tournament]) => ({
      joinDocId,
      tournament,
      card: mapParticipantTournamentToCard(joinDocId, tournament),
    }))
    .filter((entry) => !!entry.card)
), [hasAdminAccess, hasPrivateAccessForTournament, mapParticipantTournamentToCard, tournamentDocsById, tournamentMatchesSelectedGames]);

const currentParticipantTournamentCards = useMemo(() => (
  Object.entries(currentParticipantTournamentDocsById)
    .filter(([, tournament]) => tournamentMatchesSelectedGames(tournament))
    .map(([joinDocId, tournament]) => ({
      joinDocId,
      tournament,
      card: mapParticipantTournamentToCard(joinDocId, tournament),
    }))
    .filter((entry) => !!entry.card)
), [currentParticipantTournamentDocsById, mapParticipantTournamentToCard, tournamentMatchesSelectedGames]);

const activeTournamentCards = useMemo(() => (
  allVisibleTournamentCards
    .filter(({ tournament }) => isRunningTournament(tournament))
    .map(({ card }) => card)
    .sort((a, b) => getTimestampMillis(tournamentDocsById[b.joinDocId]?.startAt) - getTimestampMillis(tournamentDocsById[a.joinDocId]?.startAt))
), [allVisibleTournamentCards, getTimestampMillis, tournamentDocsById]);

const myUpcomingTournamentCards = useMemo(() => (
  currentParticipantTournamentCards
    .filter(({ tournament }) => isUpcomingCurrentParticipantTournament(tournament, currentUid, nowTick))
    .map(({ card }) => card)
    .sort((a, b) => {
      const tournamentA = currentParticipantTournamentDocsById[a.joinDocId] || a;
      const tournamentB = currentParticipantTournamentDocsById[b.joinDocId] || b;
      return getTournamentStartMillis(tournamentA, a.joinDocId) - getTournamentStartMillis(tournamentB, b.joinDocId);
    })
), [currentParticipantTournamentCards, currentParticipantTournamentDocsById, currentUid, getTournamentStartMillis, nowTick]);

const recentlyFinishedTournamentCards = useMemo(() => (
  allVisibleTournamentCards
    .filter(({ tournament }) => isRecentlyFinishedTournament(tournament, nowTick))
    .map(({ card }) => card)
    .sort((a, b) => getTournamentFinishedAtMillis(tournamentDocsById[b.joinDocId]) - getTournamentFinishedAtMillis(tournamentDocsById[a.joinDocId]))
), [allVisibleTournamentCards, nowTick, tournamentDocsById]);

useEffect(() => {
  if (!currentUid) return;

  Object.entries(favoriteTournamentRefsById || {}).forEach(([tournamentId, favorite]) => {
    const tournament =
      favoriteTournamentDocsById?.[tournamentId] ||
      participantTournamentDocsById?.[tournamentId] ||
      tournamentDocsById?.[tournamentId] ||
      getScheduledTournamentFallback(tournamentId);
    if (!tournament) return;

    const status = String(tournament.status || "open").toLowerCase();
    const isFinished = status === "finished" || !!tournament.winnerUid;
    const shouldRemove =
      isInvalidFavoriteTournament(tournament) ||
      (isFinished && !isRecentFinishedFavoriteTournament(tournament));

    if (!shouldRemove) return;

    const cleanupKey = `${currentUid}:${favorite.docId || tournamentId}`;
    if (favoriteCleanupAttemptedRef.current.has(cleanupKey)) return;
    favoriteCleanupAttemptedRef.current.add(cleanupKey);

    deleteDoc(doc(db, "users", currentUid, "favoriteTournaments", favorite.docId || tournamentId)).catch((err) => {
      favoriteCleanupAttemptedRef.current.delete(cleanupKey);
      console.error("Failed to cleanup favorite tournament:", err?.code || err?.message || "unknown");
    });
  });
}, [
  currentUid,
  favoriteTournamentDocsById,
  favoriteTournamentRefsById,
  isInvalidFavoriteTournament,
  isRecentFinishedFavoriteTournament,
  getScheduledTournamentFallback,
  participantTournamentDocsById,
  tournamentDocsById,
]);

const favoriteTournamentCards = useMemo(() => {
  const getStartSortMillis = (entry) => {
    return (
      getTournamentStartMillis(entry.tournament, entry.joinDocId) ||
      getTimestampMillis(entry.tournament?.createdAt)
    );
  };

  const getFavoriteBucket = (entry) => {
    const status = String(entry.tournament?.status || "open").toLowerCase();
    if (
      status === "running" ||
      (
        isReachableParticipantTournament(entry.tournament, currentUid, nowTick) &&
        activeTournamentIds[entry.joinDocId] !== false
      )
    ) {
      return 0;
    }
    if (isUpcomingParticipantTournament(entry.tournament, currentUid, nowTick)) return 1;
    if (status === "finished" || !!entry.tournament?.winnerUid) return 3;
    return 2;
  };

  return Object.keys(favoriteTournamentRefsById || {})
    .map((joinDocId) => {
      const tournament =
        favoriteTournamentDocsById?.[joinDocId] ||
        participantTournamentDocsById?.[joinDocId] ||
        tournamentDocsById?.[joinDocId] ||
        getScheduledTournamentFallback(joinDocId);
      if (!tournament) return null;
      if (!tournamentMatchesSelectedGames(tournament)) return null;
      if (isInvalidFavoriteTournament(tournament)) return null;

      const status = String(tournament.status || "open").toLowerCase();
      const isFinished = status === "finished" || !!tournament.winnerUid;
      if (isFinished && !isRecentFinishedFavoriteTournament(tournament)) return null;

      const card = mapParticipantTournamentToCard(joinDocId, tournament);
      return card ? { joinDocId, tournament, card } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const bucketA = getFavoriteBucket(a);
      const bucketB = getFavoriteBucket(b);
      if (bucketA !== bucketB) return bucketA - bucketB;

      if (bucketA === 3) {
        return getTournamentFinishedAtMillis(b.tournament) - getTournamentFinishedAtMillis(a.tournament);
      }

      return getStartSortMillis(a) - getStartSortMillis(b);
    })
    .map(({ card }) => card);
}, [
  activeTournamentIds,
  currentUid,
  favoriteTournamentDocsById,
  favoriteTournamentRefsById,
  getTimestampMillis,
  getTournamentStartMillis,
  getScheduledTournamentFallback,
  isInvalidFavoriteTournament,
  isRecentFinishedFavoriteTournament,
  mapParticipantTournamentToCard,
  nowTick,
  participantTournamentDocsById,
  tournamentDocsById,
  tournamentMatchesSelectedGames,
]);

const visibleTournaments = useMemo(() => {
  if (tournamentOverviewTab === "active") return activeTournamentCards;
  if (tournamentOverviewTab === "mine") return myUpcomingTournamentCards;
  if (tournamentOverviewTab === "favorites") return favoriteTournamentCards;
  if (tournamentOverviewTab === "finished") return recentlyFinishedTournamentCards;
  return tournamentsToShow;
}, [
  activeTournamentCards,
  favoriteTournamentCards,
  myUpcomingTournamentCards,
  recentlyFinishedTournamentCards,
  tournamentOverviewTab,
  tournamentsToShow,
]);

const watchedTournamentCards = useMemo(() => {
  const byDocId = new Map();
  [...tournamentsToShow, ...activeTournamentCards, ...myUpcomingTournamentCards, ...favoriteTournamentCards, ...recentlyFinishedTournamentCards]
    .forEach((tournament) => {
      const docId = getTournamentDocId(tournament);
      if (docId && !byDocId.has(docId)) byDocId.set(docId, tournament);
    });
  return Array.from(byDocId.values());
}, [
  activeTournamentCards,
  favoriteTournamentCards,
  getTournamentDocId,
  myUpcomingTournamentCards,
  recentlyFinishedTournamentCards,
  tournamentsToShow,
]);

useEffect(() => {
  const listenDocIds = Array.from(new Set(
    watchedTournamentCards
      .map((tournament) => getTournamentDocId(tournament))
      .filter(Boolean)
  ));

  if (listenDocIds.length === 0) return undefined;

  const unsubs = listenDocIds.map((joinDocId) => {
    const ref = doc(db, "tournaments", joinDocId);

    return onSnapshot(
      ref,
      (snap) => {
	        const rawData = snap.exists() ? snap.data() : { players: [] };
	        const rawPlayers = Array.isArray(rawData.players) ? rawData.players : [];
	        const leftAt = rawData.leftAt || {};
	        const hasLeft = !!currentUid && Object.prototype.hasOwnProperty.call(leftAt, currentUid);
	        const shouldKeepOptimisticJoin =
	          !!currentUid &&
	          optimisticJoinedDocIdsRef.current[joinDocId] === true &&
	          !rawPlayers.includes(currentUid) &&
	          !hasLeft;
	        const data = shouldKeepOptimisticJoin
	          ? {
	              ...rawData,
	              players: [...rawPlayers, currentUid],
	              joinedAt: {
	                ...(rawData.joinedAt || {}),
	                [currentUid]: rawData.joinedAt?.[currentUid] || true,
	              },
	            }
	          : rawData;

	        if (rawPlayers.includes(currentUid) || hasLeft) {
	          delete optimisticJoinedDocIdsRef.current[joinDocId];
	          delete optimisticJoinedDocsRef.current[joinDocId];
	        }

	        setJoinsByDocId((prev) => ({
	          ...prev,
          [joinDocId]: data,
        }));

        // ✅ Gewinner-Username laden, wenn vorhanden
if (data?.winnerUid) {
  const winnerRef = doc(db, "publicUsers", data.winnerUid);

  getDoc(winnerRef).then((userSnap) => {
    if (userSnap.exists()) {
      const username = userSnap.data()?.username || data.winnerUid;

      setWinnerNamesByUid((prev) => ({
        ...prev,
        [data.winnerUid]: username,
      }));
    }
  });
}
      },
      (err) => {
        console.error("onSnapshot tournaments failed:", err?.code || err?.message || "unknown");
      }
    );
  });

  return () => {
    unsubs.forEach((fn) => fn && fn());
  };
}, [currentUid, getTournamentDocId, watchedTournamentCards]);

useEffect(() => {
  if (!currentUid) {
    setActiveTournamentIds({});
    return undefined;
  }

  const runningJoinedDocIds = watchedTournamentCards
    .map((tournament) => getTournamentDocId(tournament))
    .filter((joinDocId) => {
      const tournamentDoc = joinsByDocId?.[joinDocId] || participantTournamentDocsById?.[joinDocId] || {};
      const status = String(tournamentDoc?.status || "open").toLowerCase();
      if (status !== "running") return false;
      return isReachableParticipantTournament(tournamentDoc, currentUid, nowTick);
    });

  if (runningJoinedDocIds.length === 0) {
    setActiveTournamentIds({});
    return undefined;
  }

  const unsubs = runningJoinedDocIds.map((joinDocId) =>
    onSnapshot(
      collection(db, "tournaments", joinDocId, "matches"),
      (snapshot) => {
        const matches = snapshot.docs.map((matchDoc) => matchDoc.data() || {});
        const userMatches = matches.filter(
          (match) => match.player1Uid === currentUid || match.player2Uid === currentUid
        );
        const tournamentDoc = joinsByDocId?.[joinDocId] || participantTournamentDocsById?.[joinDocId] || {};
        const groupStageActive =
          tournamentDoc?.groupStageEnabled === true &&
          String(tournamentDoc?.groupStageStatus || "").toLowerCase() !== "completed" &&
          !["running", "completed"].includes(String(tournamentDoc?.knockoutStatus || "").toLowerCase());
        const hasOpenMatch = userMatches.some((match) => {
          const status = String(match.status || "").toLowerCase();
          return (
            !match.winnerUid &&
            status !== "confirmed" &&
            status !== "finished" &&
            status !== "cancelled"
          );
        });
        const hasLostMatch = userMatches.some((match) => {
          const phase = String(match?.phase || "").toLowerCase();
          const isGroupMatch = phase === "group" || !!match?.groupId;
          return match.loserUid === currentUid && !(groupStageActive && isGroupMatch);
        });
        const stillActive = hasOpenMatch || userMatches.length === 0 || !hasLostMatch;

        setActiveTournamentIds((prev) => ({
          ...prev,
          [joinDocId]: stillActive,
        }));
      },
      (err) => {
        console.error("onSnapshot tournament matches failed:", err?.code || err?.message || "unknown");
        setActiveTournamentIds((prev) => ({
          ...prev,
          [joinDocId]: true,
        }));
      }
    )
  );

  return () => {
    unsubs.forEach((unsubscribe) => unsubscribe());
  };
}, [currentUid, watchedTournamentCards, getTournamentDocId, joinsByDocId, participantTournamentDocsById, nowTick]);

// ✅ Winner-Usernames nachladen (mit Debug)
useEffect(() => {
  const winnerUids = Object.values(joinsByDocId || {})
    .map((entry) => entry?.winnerUid)
    .filter(Boolean);

  // nichts zu tun
  if (winnerUids.length === 0) return;

  // nur die laden, die wir noch nicht haben
  const missing = winnerUids.filter((uid) => !winnerNamesByUid?.[uid]);
  if (missing.length === 0) return;

  (async () => {
    for (const uid of missing) {
      try {
        const userSnap = await getDoc(doc(db, "publicUsers", uid));

        if (!userSnap.exists()) {
          console.warn("Winner user doc not found:");
          continue;
        }

        const data = userSnap.data() || {};

        // ⚠️ WICHTIG: hier siehst du sofort, wie das Feld bei dir wirklich heißt:


        const username =
          data.username || data.userName || data.displayName || null;

        if (!username) {
          console.warn("No username field found for winner:");
          continue;
        }

        setWinnerNamesByUid((prev) => ({
          ...prev,
          [uid]: username,
        }));
      } catch (e) {
        console.error("Failed to load winner username (publicUsers read / rules?):", e?.code || e?.message || "unknown");
      }
    }
  })();
}, [joinsByDocId, winnerNamesByUid]);

const overviewTabs = [
  {
    id: "planned",
    label: t("tournaments.overviewTabs.planned"),
    count: 0,
  },
  {
    id: "mine",
    label: t("tournaments.overviewTabs.mine"),
    count: myUpcomingTournamentCards.length,
  },
  {
    id: "active",
    label: t("tournaments.overviewTabs.active"),
    count: activeTournamentCards.length,
  },
  {
    id: "favorites",
    label: t("tournaments.overviewTabs.favorites"),
    count: 0,
  },
  {
    id: "finished",
    label: t("tournaments.overviewTabs.finished"),
    count: 0,
  },
];

const cardStatusDefinitions = useMemo(
  () => ({
    open: getTournamentStatusVisualDefinition("open", language),
    running: getTournamentStatusVisualDefinition("running", language),
    finished: getTournamentStatusVisualDefinition("finished", language),
    didNotStart: getTournamentStatusVisualDefinition("didNotStart", language),
    closed: getTournamentStatusVisualDefinition("closed", language),
    invalid: getTournamentStatusVisualDefinition("invalid", language),
  }),
  [language]
);

const statusLegendEntries = useMemo(
  () => ["open", "running", "finished", "didNotStart", "closed", "invalid"]
    .map((key) => ({
      key,
      ...cardStatusDefinitions[key],
    })),
  [cardStatusDefinitions]
);

const statusLegendButton = (
  <button
    type="button"
    onClick={() => setStatusLegendOpen(true)}
    style={styles.statusLegendButton}
    aria-label={language === "de" ? "Turnierstatus erklären" : "Explain tournament status"}
  >
    <span aria-hidden="true">ⓘ</span>
    <span>{language === "de" ? "STATUS" : "STATUS"}</span>
  </button>
);

const emptyStateText =
  tournamentOverviewTab === "active"
    ? t("tournaments.emptyStates.active")
    : tournamentOverviewTab === "mine"
      ? t("tournaments.emptyStates.mine")
      : tournamentOverviewTab === "favorites"
        ? t("tournaments.emptyStates.favorites")
      : tournamentOverviewTab === "finished"
        ? t("tournaments.emptyStates.finished")
        : t("tournaments.emptyStates.planned");

const toggleFavoriteTournament = useCallback(async (event, tournament) => {
  event?.preventDefault?.();
  event?.stopPropagation?.();

  if (!currentUid) {
    setInfoModalTitle(language === "de" ? "Anmeldung erforderlich" : "Sign-in required");
    setInfoModalText(
      language === "de"
        ? "Melde dich an, um Turniere als Favorit zu speichern."
        : "Sign in to save tournaments as favorites."
    );
    setInfoModalProfileAction(false);
    setInfoModalOpen(true);
    return;
  }

  const tournamentId = getTournamentDocId(tournament);
  if (!tournamentId) return;

  const existingFavorite = favoriteTournamentRefsById?.[tournamentId];
  const favoriteDocId = existingFavorite?.docId || tournamentId;
  const favoriteRef = doc(db, "users", currentUid, "favoriteTournaments", favoriteDocId);

  try {
    if (existingFavorite) {
      setFavoriteTournamentRefsById((prev) => {
        const next = { ...prev };
        delete next[tournamentId];
        return next;
      });
      await deleteDoc(favoriteRef);
      return;
    }

    setFavoriteTournamentRefsById((prev) => ({
      ...prev,
      [tournamentId]: {
        docId: tournamentId,
        tournamentId,
      },
    }));
    await setDoc(
      favoriteRef,
      {
        tournamentId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }
    );
  } catch (err) {
    console.error("Failed to toggle favorite tournament:", err?.code || err?.message || "unknown");
    setInfoModalTitle(language === "de" ? "Favorit konnte nicht gespeichert werden" : "Favorite could not be saved");
    setInfoModalText(
      language === "de"
        ? "Bitte versuche es gleich noch einmal."
        : "Please try again in a moment."
    );
    setInfoModalProfileAction(false);
    setInfoModalOpen(true);
  }
}, [currentUid, favoriteTournamentRefsById, getTournamentDocId, language]);

 // ✅ SUCCESS SCREEN (wie Canva) – ersetzt Turnierliste
if (joinSuccessOpen && joinedTournament) {
  return (
    <div ref={joinSuccessScreenRef} style={styles.screen}>
      <div style={styles.successCard}>
        <div style={styles.successTitle}>
          {t("tournaments.success.title")}
        </div>

        <div style={styles.successRow}>
          <div style={styles.successIcon}>🕒</div>
          <div style={styles.successText}>
  {joinedTournament.time} {t("tournaments.common.clock")}
</div>
        </div>

        <div style={styles.successRow}>
          <div style={styles.successIcon}>🎮</div>
          <div style={styles.successText}>
            {joinedTournament.maxPlayers} {t("tournaments.common.participants")}
          </div>
        </div>

        <div style={{ height: 12 }} />

        <div style={styles.successInfoRow}>
          <div style={styles.successWarnIcon}>❗</div>
          <div style={styles.successInfoText}>
            {t("tournaments.success.info")}
          </div>
        </div>

        <button
          type="button"
      onClick={() => {
  if (joinedTournament?.dayKey) {
    const key = joinedTournament.dayKey;

    const matchingDay = dayOptions.find((day) => day.key === key);
    if (matchingDay) setSelectedDayKey(matchingDay.id);
  }

  setJoinSuccessOpen(false);
  setJoinedTournament(null);
}}

          style={styles.successBtnPrimary}
        >
         {t("tournaments.success.backToOverview")}
        </button>

<button
  type="button"
  onClick={() => {
    const fullId =
      joinedTournament?.id ||
      joinedTournament?.tournamentId ||
      joinedTournament?.joinDocId; // fallback

	    openTournamentDetail(fullId, joinedTournament, { includeCurrentUser: true });
	    setJoinSuccessOpen(false);
	  }}
  style={styles.successBtnSecondary}
>
  {t("tournaments.success.showMyTournament")}
</button>


<button
  type="button"
onClick={() => {
  const fullTournamentId =
    joinedTournament?.tournamentId ||
    joinedTournament?.docId ||
    joinedTournament?.id;

  onRequestLeave?.({
    id: fullTournamentId,
    time: joinedTournament?.time || "18:00",
    maxPlayers: joinedTournament?.maxPlayers || 32,
  });
}}


  style={styles.successBtnDanger}
>
  {t("tournaments.success.leaveTournament")}
</button>





{infoModalOpen && (
  <div
    onClick={closeInfoModal}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 10000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "380px",
        borderRadius: "18px",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <div style={{ padding: "18px 18px 14px" }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontSize: 18,
            color: "#000",
            marginBottom: 10,
          }}
        >
          {infoModalTitle}
        </div>

        <div
          style={{
            fontWeight: 900,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            fontSize: 12,
            lineHeight: 1.4,
            color: "#000",
            opacity: 0.9,
          }}
        >
          {infoModalText}
        </div>

        <button
          type="button"
          onClick={() => {
            closeInfoModal();
            if (infoModalProfileAction && typeof onCompletePlayerProfile === "function") {
              onCompletePlayerProfile();
            }
          }}
          style={{
            marginTop: 16,
            width: "100%",
            height: 54,
            border: "none",
            borderRadius: 12,
            background:
              "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)",
            color: "black",
            fontWeight: 1000,
            textTransform: "uppercase",
            letterSpacing: 1,
            cursor: "pointer",
          }}
        >
          {infoModalProfileAction ? t("profileCompletion.button") : t("common.ok")}
        </button>
      </div>
    </div>
  </div>
)}




      </div>
    </div>
  );
}



return (
  <div style={styles.screen}>


 <GameSelector
  selectedGames={selectedGames}
  onChange={updateSelectedGames}
  label={t("game.selectorLabel")}
 />

 <div style={styles.tabsRow}>
  {overviewTabs.map((tab) => {
    const active = tournamentOverviewTab === tab.id;
    const showCount = (tab.id === "active" || tab.id === "mine") && tab.count > 0;
    return (
      <button
        key={tab.id}
        type="button"
        onClick={() => setTournamentOverviewTab(tab.id)}
        style={{
          ...styles.tab,
          ...(active ? styles.tabActive : {}),
        }}
        aria-pressed={active}
      >
        <span>{tab.label}</span>
        {showCount && <span style={styles.tabCounter}>{tab.count}</span>}
      </button>
    );
 })}
 </div>

 {tournamentOverviewTab !== "planned" && (
   <div style={styles.statusLegendStandaloneRow}>
     {statusLegendButton}
   </div>
 )}

 {tournamentOverviewTab === "planned" && (
   <>
     <div style={styles.calendarHeaderRow}>
      <div style={{ ...styles.calendarHeader, margin: 0 }}>
        {selectedMonthTitle}
      </div>
      {statusLegendButton}
    </div>

    <div
      className="vq-tournament-calendar-strip"
      style={styles.calendarScroller}
      aria-label={language === "de" ? "Turnierkalender" : "Tournament calendar"}
      onScroll={handleCalendarScroll}
    >
      {dayOptions.map((day) => {
        const active = day.id === selectedDay.id;
        const hasCupMarker = cupMarkerDays[day.key] === true;
        const showCupMarker = hasCupMarker && !day.startsNewMonth;

        return (
          <button
            key={day.id}
            data-calendar-day-id={day.id}
            type="button"
            onClick={() => setSelectedDayKey(day.id)}
            style={{
              ...styles.dayCard,
              ...(active ? styles.dayCardActive : {}),
            }}
            aria-pressed={active}
          >
            {showCupMarker && (
              <span
                style={{
                  ...styles.creatorCupMarker,
                  ...(active ? styles.creatorCupMarkerActive : {}),
                }}
                aria-label="Special oder Creator Cup"
                title="Special oder Creator Cup"
              >
              </span>
            )}
            <span style={styles.dayWeekday}>{day.weekday}</span>
            <span style={styles.dayNumber}>{day.dayNumber}</span>
            <span style={styles.dayMeta}>
              {day.isToday ? t("tournaments.days.today") : day.startsNewMonth ? day.monthShortLabel : "\u00A0"}
            </span>
          </button>
        );
      })}
    </div>
   </>
 )}

    


    

      {/* Tournament Card */}
  {visibleTournaments.length === 0 && (
    <div style={styles.emptyState}>
      {emptyStateText}
    </div>
  )}

  <div className={`vq-tournament-card-grid${Capacitor.isNativePlatform() ? " is-native" : ""}`}>
  {visibleTournaments.map((tournament, idx) => {

const joinDocId = getTournamentDocId(tournament);
const tDoc = joinsByDocId?.[joinDocId] || participantTournamentDocsById?.[joinDocId] || tournamentDocsById?.[joinDocId] || {};
const cardDateKey = tDoc.dateKey || tournament.dateKey || selectedKey;
const tournamentForDetail = {
  ...tournament,
  ...tDoc,
  id: tournament.id,
  joinDocId,
  dateKey: cardDateKey,
  time: tournament.time || tDoc.time || tDoc.requestedStartTime || "",
  name: tournament.name || tDoc.name || tDoc.title,
  maxPlayers: tournament.maxPlayers || tDoc.maxPlayers || 0,
};

const now = new Date();
const [y, m, d] = String(cardDateKey || selectedKey).split("-").map(Number);
const [hh, mm] = String(tournament.time).split(":").map(Number);
const startAt = new Date(y, m - 1, d, hh, mm, 0, 0);

const hasStarted = now.getTime() >= startAt.getTime();

const playerCount = tDoc?.players?.length || 0;
const maxPlayers = Number(tournament.maxPlayers || 0);
const minPlayers = Math.floor(maxPlayers / 2);

const canStart = playerCount >= minPlayers;
const isStarted = hasStarted && canStart;

const isFull = maxPlayers > 0 && playerCount >= maxPlayers;

const isVoid = tDoc?.status === "void";

const isFinished =
  tDoc?.status === "finished" ||
  !!tDoc?.winnerUid;

let status = String(tDoc?.status || "open");

// ✅ Sonderfall:
// Turnier existiert gar nicht UND Startzeit ist vorbei
if (!tDoc?.status && hasStarted && !canStart) {
  status = "cancelled";
}

// ✅ Falls genug Spieler da sind und Zeit erreicht
if (!tDoc?.status && isStarted) {
  status = "running";
}

const joinBlocked =
  tournament?.runtimeTournamentMissing === true ||
  isFinished ||
  isVoid ||
  isFull ||
  status === "running" ||
  status === "cancelled" ||
  status === "did_not_start";

const winnerUid = tDoc?.winnerUid || null;
const winnerName = winnerUid ? (winnerNamesByUid?.[winnerUid] || "...") : null;

const isJoined =
  (tDoc?.players || []).includes(currentUid);

const hasJoinedAtEntry =
  !!currentUid &&
  !!tDoc?.joinedAt &&
  Object.prototype.hasOwnProperty.call(tDoc.joinedAt, currentUid);

const hasLeftAtEntry =
  !!currentUid &&
  !!tDoc?.leftAt &&
  Object.prototype.hasOwnProperty.call(tDoc.leftAt, currentUid);

const wasParticipant =
  isJoined || (status !== "open" && hasJoinedAtEntry && !hasLeftAtEntry);

const isActiveParticipant =
  isJoined &&
  (
    status !== "running" ||
    activeTournamentIds[joinDocId] !== false
  );

const canSpectate =
  !isActiveParticipant &&
  (!isPrivateTournament(tournament) || hasPrivateAccessForTournament(tournament) || hasAdminAccess) &&
  (
    status === "running" ||
    status === "finished" ||
    status === "void" ||
    isFinished
  );

const canOpenTournament =
  wasParticipant || canSpectate;

const prizeSet = normalizeTournamentPrizes(tournament);
const primaryPrize = prizeSet.first || tournament.prize || t("tournaments.noPrize");
const prizeLabel = primaryPrize === t("tournaments.noPrize")
  ? (language === "de" ? "Kein Preis" : "No prize")
  : primaryPrize;
const privateCard = isPrivateTournament(tournament);
const tournamentType = String(tournamentForDetail.type || tournament.type || tournamentForDetail.tournamentType || "").toLowerCase();
const myAwardCardState = getTournamentPrizeCardState({
  tournament: { ...tournament, type: tournamentType },
  tournamentId: joinDocId,
  awards: myPrizeAwards,
  requests: myPrizeRequests,
  t,
  language,
});
const tournamentIdentity = String(
  tournamentForDetail.tournamentId ||
  tournamentForDetail.id ||
  tournament.tournamentId ||
  tournament.id ||
  joinDocId ||
  ""
).toLowerCase();
const isEveningCupCard =
  tournamentType === "evening" ||
  tournamentIdentity === "evening" ||
  tournamentIdentity.endsWith("_evening");
const isNightlyCupCard =
  tournamentType === "nightly" ||
  tournamentIdentity === "nightly" ||
  tournamentIdentity.endsWith("_nightly");
const isSpecialCreatorCupCard =
  joinDocId.endsWith("_special") ||
  tournamentType === "special" ||
  tournamentType === "community" ||
  tournamentType === "creator" ||
  tournamentType === "creator_cup" ||
  tournamentType === "creatorcup" ||
  tournamentForDetail.specialCup === true ||
  tournamentForDetail.isSpecialCup === true;
const isHeroCupCard = isSpecialCreatorCupCard || isEveningCupCard || isNightlyCupCard;
const prizePoolText = String(tournamentForDetail.prizePool || tournament.prizePool || "").trim();
const shouldShowPrizePool = isSpecialCreatorCupCard && prizePoolText.length > 0;
const heroPrizeLabel = shouldShowPrizePool
  ? t("tournaments.prizePoolLabel")
  : (language === "de" ? "PREIS" : "PRIZE");
const heroPrizeValue = shouldShowPrizePool ? prizePoolText : prizeLabel;
const HeroCupIcon = isSpecialCreatorCupCard
  ? SpecialCreatorCupIcon
  : isNightlyCupCard
    ? NightlyCupIcon
    : isEveningCupCard
      ? EveningCupIcon
      : null;
const isFavorite = !!favoriteTournamentRefsById?.[joinDocId];
const contextualStartInfo = getTournamentStartDisplayInfo(tournamentForDetail, joinDocId);
const isUpcomingFavoriteCard =
  tournamentOverviewTab === "favorites" &&
  contextualStartInfo?.startMillis > nowTick &&
  !isFinished &&
  !isVoid &&
  !["running", "cancelled", "did_not_start"].includes(status);
const shouldShowContextualStartInfo =
  !!contextualStartInfo &&
  (tournamentOverviewTab === "mine" || isUpcomingFavoriteCard);

const statusBadge = (() => {
  const docData = joinsByDocId?.[joinDocId] || participantTournamentDocsById?.[joinDocId] || tournamentDocsById?.[joinDocId] || {};
  let badgeStatus = String(docData?.status || "open");

  const playerCountForStatus = Array.isArray(docData?.players)
    ? docData.players.length
    : 0;

  const maxPlayersForStatus = Number(tournament.maxPlayers || 0);
  const minPlayersForStatus = Math.floor(maxPlayersForStatus / 2);

  const [y, m, d] = String(cardDateKey || selectedKey).split("-").map(Number);
  const [hh, mm] = String(tournament.time).split(":").map(Number);

  const startAt = new Date(y, m - 1, d, hh, mm, 0, 0);
  const hasStarted = Date.now() >= startAt.getTime();
  const canStart = playerCountForStatus >= minPlayersForStatus;

  if (!docData?.status && hasStarted && !canStart) {
    badgeStatus = "cancelled";
  }

  if (!docData?.status && hasStarted && canStart) {
    badgeStatus = "running";
  }

  const statusKey = getTournamentStatusKeyForCard(
    {
      ...docData,
      status: badgeStatus,
      voided: isVoid,
    },
    {
      playerCount: playerCountForStatus,
      maxPlayers: maxPlayersForStatus,
    }
  );

  const statusDefinition = cardStatusDefinitions[statusKey] || cardStatusDefinitions.open;
  const statusLabelPrefix = language === "de" ? "Turnierstatus" : "Tournament status";

  return (
    <span
      style={{
        ...styles.statusDot,
        background: statusDefinition.style.color,
        color: statusDefinition.style.color,
        border: statusDefinition.style.dotBorder || "none",
        boxShadow: statusDefinition.style.dotBoxShadow || styles.statusDot.boxShadow,
      }}
      aria-label={`${statusLabelPrefix}: ${statusDefinition.ariaLabel}`}
      title={statusDefinition.label}
    />
  );
})();


  return (
    <div className="vq-tournament-card-grid-item" key={joinDocId}>
      {shouldShowContextualStartInfo && (
        <div style={styles.contextualStartHeading}>
          <div style={styles.contextualStartPrimary}>{contextualStartInfo.label}</div>
          {contextualStartInfo.countdown && (
            <div style={styles.contextualStartCountdown}>{contextualStartInfo.countdown}</div>
          )}
        </div>
      )}

    <div
      className="vq-tournament-card"
      onClick={() => {
  if (!canOpenTournament) return;
		  openTournamentDetail(joinDocId, tournamentForDetail, { includeCurrentUser: wasParticipant });
	}}
      style={{
        ...styles.card,
        ...(isHeroCupCard ? styles.specialCreatorCard : null),
        marginBottom: 0,
        cursor: canOpenTournament ? "pointer" : "default",
        opacity: isJoined ? 1 : 1,
      }}
    >
    {HeroCupIcon && (
      <div aria-hidden="true" style={styles.specialCreatorCupEmblem}>
        <HeroCupIcon size="100%" />
      </div>
    )}


    <div
      style={{
        ...styles.cardTop,
        ...(isHeroCupCard ? styles.specialCreatorCardTop : null),
      }}
    >
      {isHeroCupCard ? (
        <>
          <div style={styles.specialCreatorTitleRow}>
            <span style={styles.specialCreatorNameCluster}>
              <span className="vq-tournament-card-title" style={{ ...styles.cardTitle, ...styles.specialCreatorCardTitle }}>
                {tournament.name}
              </span>
              <button
                type="button"
                aria-label={
                  isFavorite
                    ? (language === "de" ? "Favorit entfernen" : "Remove favorite")
                    : (language === "de" ? "Als Favorit speichern" : "Save as favorite")
                }
                title={
                  isFavorite
                    ? (language === "de" ? "Favorit entfernen" : "Remove favorite")
                    : (language === "de" ? "Als Favorit speichern" : "Save as favorite")
                }
                onClick={(event) => toggleFavoriteTournament(event, tournament)}
                style={{ ...styles.favoriteButton, ...styles.heroCupFavoriteButton }}
              >
                {isFavorite ? (
                  <FaStar style={styles.favoriteIcon} />
                ) : (
                <FaRegStar style={styles.favoriteIcon} />
                )}
              </button>
            </span>
            {statusBadge}
          </div>
        </>
      ) : (
        <>
          <div style={styles.cardTopLeft}>
            <span style={styles.gameIcon}>🎮</span>
            <span className="vq-tournament-card-title" style={styles.cardTitle}>{tournament.name}</span>
            <button
              type="button"
              aria-label={
                isFavorite
                  ? (language === "de" ? "Favorit entfernen" : "Remove favorite")
                  : (language === "de" ? "Als Favorit speichern" : "Save as favorite")
              }
              title={
                isFavorite
                  ? (language === "de" ? "Favorit entfernen" : "Remove favorite")
                  : (language === "de" ? "Als Favorit speichern" : "Save as favorite")
              }
              onClick={(event) => toggleFavoriteTournament(event, tournament)}
              style={styles.favoriteButton}
            >
              {isFavorite ? (
                <FaStar style={styles.favoriteIcon} />
              ) : (
                <FaRegStar style={styles.favoriteIcon} />
              )}
            </button>
          </div>
          {statusBadge}
        </>
      )}

    </div>

    {isHeroCupCard && (
      <div className="vq-hero-cup-price" style={styles.specialCreatorHeroPrice}>
        <div style={styles.specialCreatorHeroPriceLabel}>
          {heroPrizeLabel}
        </div>
        <div className="vq-hero-cup-price__value" style={styles.specialCreatorHeroPriceValue}>
          {heroPrizeValue}
        </div>
      </div>
    )}

  {!isHeroCupCard && (
    <div style={styles.prizeBar}>
      <div style={styles.prizeBarMain}>
        {prizeLabel}
      </div>
    </div>
  )}

{isFinished ? (
  <div
    style={{
      margin: "14px",
      borderRadius: 14,
      border: "2px solid rgba(255,255,255,0.35)",
      background: "rgba(0,0,0,0.15)",
      padding: "18px 14px",
      textAlign: "center",
      color: "white",
      fontWeight: 1000,
      letterSpacing: 1,
      textTransform: "uppercase",
      fontSize: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
    }}
  >
    <CrownIcon size={22} />
    <span>{winnerName || winnerUid || t("tournaments.card.noWinner")}</span>
  </div>
) : (
  <div style={styles.statsRow}>
   <div className="vq-tournament-player-stat" style={styles.statBox}>
  <div className="vq-tournament-player-stat__icon" style={styles.statIcon}>
    <CommunityIcon size={24} />
  </div>

  <>
    <div className="vq-tournament-player-stat__count" style={styles.statMain}>
      {(playerCount || 0)}/{tournament.maxPlayers}
    </div>

    <div className="vq-tournament-player-stat__label" style={styles.statSub}>
      {t("tournaments.card.players")}
    </div>
  </>
</div>

    <div style={styles.statBox}>
      <div style={styles.statIcon}>🕒</div>
      <div style={styles.statMain}>{tournament.time}</div>
      <div style={styles.statSub}>{t("tournaments.card.clock")}</div>
    </div>

    <div style={{ ...styles.statBox, ...styles.statBoxLast }}>
      <div style={styles.statIcon}>
        <CoinIcon size={24} />
      </div>
      <div style={styles.statMain}>{tournament.entryCoins || 500}</div>
      <div style={styles.statSub}>{t("tournaments.card.coins")}</div>
    </div>
  </div>
)}

<div style={styles.cardActionsRow}>
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      setRulesPreviewTournament(tournamentForDetail);
      setRulesPreviewTermsOpen(false);
    }}
    style={styles.cardActionButton}
  >
    <span style={styles.cardActionContent}>
      <FaRegFileAlt style={styles.cardActionIcon} aria-hidden="true" />
      <span style={styles.cardActionText}>{t("tournaments.card.viewRules")}</span>
    </span>
    <FaChevronRight style={styles.cardActionChevron} aria-hidden="true" />
  </button>
  <button
    type="button"
    onClick={(event) => {
      event.stopPropagation();
      setPrizeModalTournament({
        name: tournament.name,
        prizes: prizeSet,
      });
    }}
    style={{
      ...styles.cardActionButton,
      ...styles.cardActionButtonRight,
    }}
  >
    <span style={styles.cardActionContent}>
      <FaInfoCircle style={styles.cardActionIcon} aria-hidden="true" />
      <span style={styles.cardActionText}>{t("tournaments.card.viewAllPrizes")}</span>
    </span>
    <FaChevronRight style={styles.cardActionChevron} aria-hidden="true" />
  </button>
</div>

{myAwardCardState && (
  <div style={styles.prizeAwardPanel} onClick={(event) => event.stopPropagation()}>
    <div style={styles.prizeAwardText}>
      {myAwardCardState.claimable ? myAwardCardState.wonText : myAwardCardState.statusLabel}
    </div>
    {(myAwardCardState.claimable || myAwardCardState.correctable) && (
      <button
        type="button"
        style={styles.prizeAwardButton}
        onClick={(event) => {
          event.stopPropagation();
          onOpenMyPrizes?.();
        }}
      >
        {myAwardCardState.correctable ? t("myPrizes.actions.correctDetails") : myAwardCardState.actionLabel}
      </button>
    )}
  </div>
)}

   {canSpectate ? (

<button
  style={{ ...styles.cta, opacity: 1, cursor: "pointer" }}
  onClick={(e) => {
    e.stopPropagation();
	    openTournamentDetail(joinDocId, tournamentForDetail, { includeCurrentUser: false });
  }}
>
  <span>{t("tournaments.card.followTournament")}</span>
  <span style={styles.ctaArrow}>›</span>
</button>



) : wasParticipant ? (

<button
  style={{ ...styles.cta, opacity: 0.6, cursor: "pointer" }}
  onClick={() => {
    // absichtlich NICHT stopPropagation:
    // der Klick soll auch dann funktionieren, wenn man genau auf den Button klickt.
    // Wir navigieren direkt in die Lobby:
	    openTournamentDetail(joinDocId, tournamentForDetail, { includeCurrentUser: true });
  }}
>
  <span>{t("tournaments.card.joined")}</span>
</button>



) : (


<button
  style={{
    ...styles.cta,
    opacity: joinBlocked ? 0.45 : 1,
    cursor:
      joinBlocked && canOpenTournament
        ? "pointer"
        : joinBlocked
        ? "not-allowed"
        : "pointer",
  }}
  disabled={false}
  onClick={(e) => {
  // ✅ Finished/Void soll die Detailansicht öffnen
  if (joinBlocked && canOpenTournament) {
	    openTournamentDetail(joinDocId, tournamentForDetail, { includeCurrentUser: wasParticipant });
    return;
  }

  // ❌ Nur beim echten Join Klick blockieren
  e.stopPropagation();

  // ❌ Join weiterhin gesperrt
  if (joinBlocked) return;

  if (privateCard && !hasPrivateAccessForTournament(tournament)) {
    openPrivateJoinModal(tournament);
    return;
  }

  // ✅ Normaler Join
  setSelectedJoinTournament(tournament);
  setJoinModalOpen(true);
}}
>
  <span style={styles.ctaLabel}>
    {privateCard && <FaLock style={styles.ctaLockIcon} aria-hidden="true" />}
    {joinBlocked ? t("tournaments.card.joinNotPossible") : t("tournaments.card.joinTournament")}
  </span>
  {!joinBlocked && <span style={styles.ctaArrow}>›</span>}
</button>



)}



  </div>
    </div>
  );
})}
  </div>

{/* JOIN MODAL (Custom statt window.confirm) */}
{joinModalOpen && selectedJoinTournament && (
  <div
    onClick={() => {
      setJoinModalOpen(false);
      setSelectedJoinTournament(null);
    }}
	    style={{
	      position: "fixed",
	      inset: 0,
	      background: "rgba(0,0,0,0.72)",
	      zIndex: 9999,
	      display: "flex",
	      alignItems: "center",
	      justifyContent: "center",
	      padding: "calc(24px + env(safe-area-inset-top)) 18px calc(24px + env(safe-area-inset-bottom))",
	      boxSizing: "border-box",
	      overflowY: "auto",
	    }}
	  >
	    <div
	      onClick={(e) => e.stopPropagation()}
	      style={{
	        width: "100%",
	        maxWidth: "420px",
	        borderRadius: "24px",
	        overflow: "hidden",
	        background:
	          "linear-gradient(145deg, rgba(23,12,74,0.98) 0%, rgba(52,18,106,0.98) 100%)",
	        border: "2px solid rgba(255,255,255,0.26)",
	        boxShadow:
	          "0 26px 70px rgba(0,0,0,0.52), 0 0 34px rgba(255,0,170,0.24), inset 0 1px 0 rgba(255,255,255,0.16)",
	        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
	        maxHeight: "calc(100dvh - 48px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
	        overflowY: "auto",
	        WebkitOverflowScrolling: "touch",
	      }}
	    >
	      <div style={{ padding: "22px 20px 16px" }}>
	        <div
	          style={{
	            fontWeight: 1000,
	            letterSpacing: 1.2,
	            textTransform: "uppercase",
	            fontSize: 20,
	            color: "white",
	            marginBottom: 16,
	            lineHeight: 1.2,
	            textAlign: "center",
	          }}
	        >
          {language === "de"
  ? `Willst du für ${selectedJoinTournament.entryCoins ?? 500} Coins an diesem Turnier teilnehmen?`
  : `Do you want to join this tournament for ${selectedJoinTournament.entryCoins ?? 500} coins?`}
        </div>

	        <div style={{ display: "grid", gap: 12, marginBottom: 18 }}>
	          <div style={{
	            display: "flex",
	            alignItems: "center",
	            gap: 12,
	            borderRadius: 16,
	            border: "1px solid rgba(255,255,255,0.18)",
	            background: "rgba(255,255,255,0.08)",
	            padding: "12px 14px",
	          }}>
	            <div style={{ fontSize: 22 }}>🕒</div>
	            <div style={{ fontWeight: 900, color: "white" }}>
	              {selectedJoinTournament.time} {t("tournaments.common.clock")}
	            </div>
	          </div>

	          <div style={{
	            display: "flex",
	            alignItems: "center",
	            gap: 12,
	            borderRadius: 16,
	            border: "1px solid rgba(255,255,255,0.18)",
	            background: "rgba(255,255,255,0.08)",
	            padding: "12px 14px",
	          }}>
	            <div style={{ fontSize: 22 }}>🎮</div>
	            <div style={{ fontWeight: 900, color: "white" }}>
	  {(() => {
	    const selectedJoinDocId = getTournamentDocId(selectedJoinTournament);
	    const selectedJoinDoc = joinsByDocId?.[selectedJoinDocId] || tournamentDocsById?.[selectedJoinDocId] || {};
	    const selectedPlayerCount = Array.isArray(selectedJoinDoc?.players)
	      ? selectedJoinDoc.players.length
	      : 0;
	    return `${selectedPlayerCount}/${selectedJoinTournament.maxPlayers} ${t("tournaments.common.participants")}`;
	  })()}
</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 14 }}>
          <button
            type="button"


	        onClick={async () => {
  if (joiningTournament) return;

	  const ok = await joinFixedTournamentFirestore(selectedJoinTournament);

  // ❌ Join fehlgeschlagen -> Modal schließen, aber KEIN Success, KEINE Lobby
  if (!ok) {
    setJoinModalOpen(false);
    setSelectedJoinTournament(null);
    return;
  }

  // ✅ Join erfolgreich -> Success Screen
  setJoinModalOpen(false);

  const joinDocId = getTournamentDocId(selectedJoinTournament);
  markTournamentJoinedLocally(joinDocId, selectedJoinTournament);

  setJoinedTournament({
    ...selectedJoinTournament,
    id: joinDocId,
    tournamentId: joinDocId,
    dayKey: selectedKey,
  });

  setJoinSuccessOpen(true);
  setSelectedJoinTournament(null);
	}}
            disabled={joiningTournament}


	            style={{
	              flex: 1,
	              border: "2px solid rgba(255,255,255,0.55)",
	              borderRadius: 999,
	              padding: "12px 10px",
	              fontWeight: 1000,
	              letterSpacing: 1,
		              background: joiningTournament
                    ? "rgba(255,255,255,0.16)"
                    : "linear-gradient(135deg, #00c853 0%, #009624 100%)",
		              color: "white",
		              cursor: joiningTournament ? "wait" : "pointer",
                  opacity: joiningTournament ? 0.72 : 1,
	              boxShadow: "0 10px 24px rgba(0,200,83,0.24)",
	            }}
	          >
	            {joiningTournament ? "..." : t("common.yes")}
          </button>

          <button
            type="button"
	            onClick={() => {
	              if (joiningTournament) return;
	              setJoinModalOpen(false);
	              setSelectedJoinTournament(null);
	            }}
              disabled={joiningTournament}
	            style={{
	              flex: 1,
	              border: "2px solid rgba(255,255,255,0.34)",
	              borderRadius: 999,
	              padding: "12px 10px",
	              fontWeight: 1000,
	              letterSpacing: 1,
	              background: "rgba(255,255,255,0.10)",
	              color: "white",
		              cursor: joiningTournament ? "wait" : "pointer",
                  opacity: joiningTournament ? 0.58 : 1,
	            }}
          >
            {t("common.no")}
          </button>
        </div>
      </div>

<div
	  style={{
	    margin: "0 20px 20px",
	    padding: "12px 14px",
	    borderRadius: 16,
	    border: "1px solid rgba(255,90,90,0.42)",
	    background: "rgba(255,59,48,0.16)",
	    fontWeight: 900,
	    letterSpacing: 1,
	    textTransform: "uppercase",
	    fontSize: 12,
	    lineHeight: 1.35,
	    color: "#ffd7d7",
	    textAlign: "center",
	  }}
>
  ⚠️ {t("tournaments.joinModal.warning")}
</div>

    </div>
  </div>
)}

{/* PRIVATE JOIN CODE MODAL */}
{privateJoinModalOpen && selectedJoinTournament && (
  <PrivateTournamentCodeModal
    title={t("tournaments.privateJoin.title")}
    text={t("tournaments.privateJoin.text")}
    code={privateJoinCode}
    error={privateJoinError}
    loading={privateJoinLoading}
    deleteLabel={t("community.privateTournaments.delete")}
    confirmLabel={
      privateJoinLoading
        ? t("community.privateTournaments.checking")
        : t("tournaments.privateJoin.confirm")
    }
    cancelLabel={t("community.privateTournaments.cancel")}
    onDigit={(digit) => {
      setPrivateJoinError("");
      setPrivateJoinCode((current) => (current.length >= 6 ? current : `${current}${digit}`));
    }}
    onDelete={() => {
      setPrivateJoinError("");
      setPrivateJoinCode((current) => current.slice(0, -1));
    }}
    onConfirm={submitPrivateJoinCode}
    onCancel={closePrivateJoinModal}
  />
)}

{infoModalOpen && (
  <div
    onClick={closeInfoModal}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 10000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "18px",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: "380px",
        borderRadius: "18px",
        overflow: "hidden",
        background: "linear-gradient(180deg, #f2e9ff 0%, #e7d6ff 100%)",
        boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <div style={{ padding: "18px 18px 14px" }}>
        <div
          style={{
            fontWeight: 1000,
            letterSpacing: 1.2,
            textTransform: "uppercase",
            fontSize: 18,
            color: "#000",
            marginBottom: 10,
          }}
        >
          {infoModalTitle}
        </div>

        <div
          style={{
            fontWeight: 900,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            fontSize: 12,
            lineHeight: 1.4,
            color: "#000",
            opacity: 0.9,
          }}
        >
          {infoModalText}
        </div>

        <button
          type="button"
          onClick={() => {
            closeInfoModal();
            if (infoModalProfileAction && typeof onCompletePlayerProfile === "function") {
              onCompletePlayerProfile();
            }
          }}
          style={{
            marginTop: 16,
            width: "100%",
            height: 54,
            border: "none",
            borderRadius: 12,
            background:
              "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)",
            color: "black",
            fontWeight: 1000,
            textTransform: "uppercase",
            letterSpacing: 1,
            cursor: "pointer",
          }}
        >
          {infoModalProfileAction ? t("profileCompletion.button") : t("common.ok")}
        </button>
      </div>
    </div>
  </div>
)}

{statusLegendOpen && (
  <div
    onClick={() => setStatusLegendOpen(false)}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.66)",
      zIndex: 10044,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "calc(20px + env(safe-area-inset-top)) 18px calc(20px + env(safe-area-inset-bottom))",
      boxSizing: "border-box",
    }}
  >
    <div
      onClick={(event) => event.stopPropagation()}
      style={styles.statusLegendModal}
    >
      <div style={styles.statusLegendTitle}>
        {language === "de" ? "TURNIERSTATUS" : "TOURNAMENT STATUS"}
      </div>
      <div style={styles.statusLegendList}>
        {statusLegendEntries.map((entry) => (
          <div key={entry.key} style={styles.statusLegendItem}>
            <span
              style={{
                ...styles.statusDot,
                background: entry.style.color,
                color: entry.style.color,
                border: entry.style.dotBorder || "none",
                boxShadow: entry.style.dotBoxShadow || styles.statusDot.boxShadow,
              }}
              aria-hidden="true"
            />
            <span>{entry.label}</span>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setStatusLegendOpen(false)}
        style={styles.statusLegendCloseButton}
      >
        {t("common.close")}
      </button>
    </div>
  </div>
)}

{prizeModalTournament && (
  <div
    onClick={() => setPrizeModalTournament(null)}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.66)",
      zIndex: 10045,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "calc(20px + env(safe-area-inset-top)) 18px calc(20px + env(safe-area-inset-bottom))",
      boxSizing: "border-box",
    }}
  >
    <div
      onClick={(event) => event.stopPropagation()}
      style={{
        width: "min(390px, 100%)",
        borderRadius: 22,
        border: "2px solid rgba(255,255,255,0.24)",
        background: "linear-gradient(145deg, rgba(31,15,84,0.98), rgba(65,20,118,0.98))",
        boxShadow: "0 24px 65px rgba(0,0,0,0.5), 0 0 30px rgba(255,0,170,0.22)",
        padding: 20,
        boxSizing: "border-box",
        color: "white",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 1000, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
        {t("tournaments.prizesModal.title")}
      </div>
      <div style={{ fontSize: 13, fontWeight: 900, color: "rgba(255,255,255,0.72)", marginBottom: 16 }}>
        {prizeModalTournament.name}
      </div>
      {[
        ["first", t("tournaments.prizesModal.first")],
        ["second", t("tournaments.prizesModal.second")],
        ["third", t("tournaments.prizesModal.third")],
      ].map(([rank, label]) => (
        <div key={rank} style={styles.prizeModalRow}>
          <span>{label}</span>
          <strong>{prizeModalTournament.prizes?.[rank] || t("tournaments.prizesModal.empty")}</strong>
        </div>
      ))}
      <button
        type="button"
        onClick={() => setPrizeModalTournament(null)}
        style={{
          marginTop: 16,
          width: "100%",
          minHeight: 48,
          border: "2px solid rgba(255,255,255,0.28)",
          borderRadius: 14,
          background: "rgba(255,255,255,0.1)",
          color: "white",
          fontWeight: 1000,
          textTransform: "uppercase",
          letterSpacing: 1,
          cursor: "pointer",
        }}
      >
        {t("common.close")}
      </button>
    </div>
  </div>
)}

{rulesPreviewTournament && (
  <div
    onClick={() => {
      setRulesPreviewTournament(null);
      setRulesPreviewTermsOpen(false);
    }}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.68)",
      zIndex: 10050,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "calc(20px + env(safe-area-inset-top)) 18px calc(20px + env(safe-area-inset-bottom))",
      boxSizing: "border-box",
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(440px, 100%)",
        maxHeight: "calc(100dvh - 40px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
        overflowY: "auto",
        WebkitOverflowScrolling: "touch",
        borderRadius: 22,
        border: "2px solid rgba(255,255,255,0.24)",
        background: "linear-gradient(145deg, rgba(23,12,74,0.98), rgba(52,18,106,0.98))",
        boxShadow: "0 26px 70px rgba(0,0,0,0.52), 0 0 34px rgba(255,0,170,0.24)",
        padding: "18px 18px calc(112px + env(safe-area-inset-bottom))",
        boxSizing: "border-box",
        fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
      }}
    >
      <CompetitionRulesView
        {...buildTournamentRulesViewModel(rulesPreviewTournament, language, t)}
        showCompetitionTerms={rulesPreviewTermsOpen}
        onOpenCompetitionTerms={() => setRulesPreviewTermsOpen(true)}
        onBackFromCompetitionTerms={() => setRulesPreviewTermsOpen(false)}
      />
      <button
        type="button"
        onClick={() => {
          setRulesPreviewTournament(null);
          setRulesPreviewTermsOpen(false);
        }}
        style={{
          marginTop: 16,
          width: "100%",
          height: 52,
          border: "none",
          borderRadius: 14,
          background: "linear-gradient(90deg, rgba(255,0,170,0.95), rgba(160,60,255,0.95))",
          color: "white",
          fontWeight: 1000,
          textTransform: "uppercase",
          letterSpacing: 1,
          cursor: "pointer",
        }}
      >
        {t("common.close")}
      </button>
    </div>
  </div>
)}
    
    </div>
  );
}

const styles = {

  screen: {
  width: "100%",
  maxWidth: "100%",
  height: "100%",
  padding: "0 4px 90px",
  boxSizing: "border-box",
  overflowY: "auto",
  overflowX: "hidden",
},

tabsRow: {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 0,
  marginBottom: 20,
  width: "100%",
  maxWidth: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  boxSizing: "border-box",
},

tab: {
  flex: "0 0 auto",
  minHeight: 48,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.2)",
  background:
    "linear-gradient(135deg, rgba(255,0,127,0.22) 0%, rgba(122,43,255,0.24) 100%)",
  color: "white",
  fontWeight: 1000,
  letterSpacing: 0.2,
  fontSize: 12,
  padding: "10px 12px",
  boxSizing: "border-box",
  minWidth: 84,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
  cursor: "pointer",
},

calendarHeader: {
  margin: "2px 2px 10px",
  color: "rgba(255,255,255,0.86)",
  fontWeight: 1000,
  fontSize: 14,
  letterSpacing: 1.2,
  textTransform: "uppercase",
},

calendarHeaderRow: {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  margin: "2px 2px 10px",
},

statusLegendStandaloneRow: {
  display: "flex",
  justifyContent: "flex-end",
  margin: "-8px 2px 12px",
},

statusLegendButton: {
  border: "1px solid rgba(255,255,255,0.22)",
  borderRadius: 999,
  background: "rgba(255,255,255,0.08)",
  color: "rgba(255,255,255,0.84)",
  minHeight: 30,
  padding: "6px 10px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
  fontWeight: 1000,
  fontSize: 10,
  letterSpacing: 0.8,
  textTransform: "uppercase",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
},

calendarScroller: {
  display: "flex",
  alignItems: "stretch",
  gap: 10,
  width: "100%",
  maxWidth: "100%",
  margin: "0 0 20px",
  padding: "2px 2px 8px",
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  overscrollBehaviorX: "contain",
  scrollbarWidth: "none",
  boxSizing: "border-box",
},

dayCard: {
  position: "relative",
  flex: "0 0 58px",
  minWidth: 58,
  minHeight: 78,
  borderRadius: 18,
  border: "1px solid rgba(255,255,255,0.18)",
  background:
    "linear-gradient(145deg, rgba(16,8,58,0.88) 0%, rgba(31,13,86,0.82) 100%)",
  color: "white",
  display: "grid",
  gridTemplateRows: "auto auto auto",
  justifyItems: "center",
  alignContent: "center",
  gap: 3,
  padding: "9px 7px 8px",
  boxSizing: "border-box",
  fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
  boxShadow: "0 10px 20px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.08)",
  cursor: "pointer",
  touchAction: "manipulation",
},

dayCardActive: {
  border: "1px solid rgba(255,255,255,0.58)",
  background:
    "linear-gradient(135deg, rgba(255,0,127,0.95) 0%, rgba(122,43,255,0.96) 100%)",
  boxShadow:
    "0 0 18px rgba(255,0,170,0.34), 0 14px 28px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.20)",
},

dayWeekday: {
  fontSize: 11,
  fontWeight: 1000,
  lineHeight: 1,
  color: "rgba(255,255,255,0.78)",
  letterSpacing: 0.6,
},

dayNumber: {
  fontSize: 24,
  fontWeight: 1000,
  lineHeight: 1,
  color: "#fff",
},

dayMeta: {
  minHeight: 11,
  fontSize: 9,
  fontWeight: 1000,
  lineHeight: 1,
  color: "rgba(255,255,255,0.72)",
  letterSpacing: 0.4,
  textTransform: "uppercase",
},

creatorCupMarker: {
  position: "absolute",
  left: "50%",
  bottom: 5,
  width: 8,
  height: 8,
  borderRadius: 999,
  display: "block",
  transform: "translateX(-50%)",
  background: "#ff304f",
  border: "1px solid rgba(255,255,255,0.76)",
  lineHeight: 1,
  boxShadow: "0 0 10px rgba(255,48,79,0.42)",
},

creatorCupMarkerActive: {
  background: "#ff304f",
  border: "1px solid rgba(255,255,255,0.88)",
},
 

  titleWrap: {
    position: "relative",
    marginTop: 0,
    marginBottom: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  titleText: {
    fontWeight: 900,
    fontStyle: "italic",
    letterSpacing: 2,
    fontSize: 34,
    color: "#ff33cc",
    textShadow: "0 8px 25px rgba(255, 0, 200, 0.35)",
  },
  titleLineLeft: {
    flex: 1,
    height: 3,
    borderRadius: 99,
    background:
      "linear-gradient(90deg, rgba(255,51,204,0) 0%, rgba(255,51,204,1) 100%)",
  },
  titleLineRight: {
    flex: 1,
    height: 3,
    borderRadius: 99,
    background:
      "linear-gradient(90deg, rgba(153,0,255,1) 0%, rgba(153,0,255,0) 100%)",
  },


  tabActive: {
    background:
      "linear-gradient(135deg, rgba(255,0,127,0.9) 0%, rgba(122,43,255,0.92) 100%)",
    border: "1px solid rgba(255,255,255,0.62)",
  boxShadow:
    "0 0 18px rgba(255,0,170,0.42), 0 12px 26px rgba(0,0,0,0.25)",
  },

  tabCounter: {
    minWidth: 18,
    height: 18,
    flex: "0 0 auto",
    padding: "0 5px",
    borderRadius: 99,
    background: "rgba(255,68,88,0.95)",
    color: "white",
    fontSize: 11,
    lineHeight: "18px",
    textAlign: "center",
    boxSizing: "border-box",
    boxShadow: "0 0 10px rgba(255,68,88,0.35)",
  },

  emptyState: {
    width: "100%",
    margin: "10px 0 18px",
    padding: "18px 16px",
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.18)",
    background: "rgba(18,14,70,0.58)",
    color: "rgba(255,255,255,0.76)",
    fontSize: 14,
    fontWeight: 900,
    lineHeight: 1.35,
    boxSizing: "border-box",
  },

  card: {
    position: "relative",
    borderRadius: 18,
    border: "2px solid rgba(255,255,255,0.45)",
    background:
      "linear-gradient(180deg, rgba(170,90,255,0.65) 0%, rgba(120,60,220,0.55) 100%)",
    boxShadow: "0 18px 45px rgba(0,0,0,0.35)",
    overflow: "visible",
  },
  specialCreatorCard: {
    background:
      "linear-gradient(180deg, rgba(94,48,178,0.74) 0%, rgba(94,48,178,0.74) 100%)",
  },
  specialCreatorCupEmblem: {
    position: "absolute",
    top: 10,
    left: 16,
    width: "clamp(60px, 16vw, 90px)",
    height: "clamp(60px, 16vw, 90px)",
    pointerEvents: "none",
    zIndex: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
    filter: "drop-shadow(0 8px 12px rgba(0,0,0,0.28))",
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 14px",
    background: "rgba(255,255,255,0.08)",
    borderRadius: "16px 16px 0 0",
  },
  specialCreatorCardTop: {
    borderRadius: "16px 16px 0 0",
    background: "transparent",
    padding: "12px 10px 6px clamp(90px, 24vw, 138px)",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  cardTopLeft: { display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "1 1 auto" },
  specialCreatorCardTopLeft: {
    paddingLeft: 0,
  },
  specialCreatorTitleRow: {
    minWidth: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  specialCreatorNameCluster: {
    minWidth: 0,
    flex: "1 1 auto",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  specialCreatorCardTitle: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "block",
    fontSize: "clamp(14px, 4vw, 19px)",
    lineHeight: 1.1,
  },
  specialCreatorMetaRow: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
  },
  gameIcon: { fontSize: 18, opacity: 0.95, flexShrink: 0 },
  cardTitle: {
    flex: "1 1 auto",
    fontWeight: 900,
    letterSpacing: 1,
    color: "white",
    fontSize: 18,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contextualStartHeading: {
    margin: "12px 2px 7px",
    padding: "0 2px",
    color: "rgba(255,255,255,0.86)",
    lineHeight: 1.25,
  },
  contextualStartPrimary: {
    fontSize: 12,
    fontWeight: 1000,
    letterSpacing: 1.1,
    textTransform: "uppercase",
  },
  contextualStartCountdown: {
    marginTop: 3,
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.3,
    color: "rgba(255,255,255,0.66)",
  },
  specialCreatorHeroPrice: {
    margin: "0 14px 12px",
    padding: "4px clamp(8px, 3vw, 12px) 0",
    minHeight: 58,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    pointerEvents: "none",
  },
  specialCreatorHeroPriceLabel: {
    fontSize: 10,
    lineHeight: "12px",
    fontWeight: 1000,
    letterSpacing: 1.4,
    color: "rgba(255,255,255,0.68)",
    textTransform: "uppercase",
  },
  specialCreatorHeroPriceValue: {
    margin: "2px auto 0",
    maxWidth: "min(100%, 460px)",
    fontSize: "clamp(19px, 5vw, 24px)",
    lineHeight: 1.1,
    fontWeight: 1000,
    letterSpacing: 0.4,
    textAlign: "center",
    textTransform: "uppercase",
    overflowWrap: "anywhere",
  },
  favoriteButton: {
    width: 36,
    height: 36,
    minWidth: 36,
    border: "none",
    borderRadius: 999,
    background: "transparent",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    cursor: "pointer",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  },
  favoriteIcon: {
    fontSize: 19,
    color: "#fff",
    filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
  },
  heroCupFavoriteButton: {
    width: 28,
    height: 28,
    minWidth: 28,
    flex: "0 0 auto",
  },
  ctaLabel: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    minWidth: 0,
  },
  ctaLockIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
 statusDot: {
  width: 12,
  height: 12,
  minWidth: 12,
  borderRadius: 999,
  display: "inline-block",
  flex: "0 0 auto",
  boxShadow: "0 0 10px currentColor",
},
statusOpen: {
  border: "2px solid rgba(0,255,120,0.8)",
  color: "rgba(0,255,120,1)",
},
statusClosed: {
  border: "2px solid rgba(255,60,60,0.85)",
  color: "rgba(255,60,60,1)",
},

statusFinished: {
  border: "2px solid rgba(241, 196, 15, 0.95)", // gold
  color: "rgba(241, 196, 15, 1)",
},

statusLegendModal: {
  width: "min(340px, 100%)",
  borderRadius: 20,
  border: "2px solid rgba(255,255,255,0.24)",
  background: "linear-gradient(145deg, rgba(31,15,84,0.98), rgba(65,20,118,0.98))",
  boxShadow: "0 24px 65px rgba(0,0,0,0.5), 0 0 30px rgba(255,0,170,0.22)",
  padding: 18,
  boxSizing: "border-box",
  color: "white",
  fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
},

statusLegendTitle: {
  fontSize: 20,
  fontWeight: 1000,
  textTransform: "uppercase",
  letterSpacing: 1,
  marginBottom: 14,
},

statusLegendList: {
  display: "grid",
  gap: 10,
},

statusLegendItem: {
  minHeight: 34,
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.14)",
  background: "rgba(255,255,255,0.08)",
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  boxSizing: "border-box",
  fontSize: 13,
  fontWeight: 1000,
  letterSpacing: 0.8,
  textTransform: "uppercase",
},

statusLegendCloseButton: {
  marginTop: 16,
  width: "100%",
  minHeight: 48,
  border: "2px solid rgba(255,255,255,0.28)",
  borderRadius: 14,
  background: "rgba(255,255,255,0.1)",
  color: "white",
  fontWeight: 1000,
  textTransform: "uppercase",
  letterSpacing: 1,
  cursor: "pointer",
},

  prizeBar: {
    margin: "8px 14px 10px",
    borderRadius: 10,
    border: "2px solid rgba(255,255,255,0.35)",
    background: "rgba(255,255,255,0.10)",
    textAlign: "center",
    padding: "8px 10px",
    fontWeight: 900,
    letterSpacing: 1,
    color: "white",
    fontSize: 17,
  },
  prizeBarMain: {
    lineHeight: 1.15,
  },
  prizeAwardPanel: {
    margin: "0 14px 12px",
    padding: "12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.26)",
    background: "linear-gradient(90deg, rgba(255,8,126,0.24), rgba(143,45,255,0.22))",
    display: "grid",
    gap: 10,
  },
  prizeAwardText: {
    color: "white",
    fontSize: 14,
    lineHeight: 1.25,
    fontWeight: 1000,
    textTransform: "uppercase",
    textAlign: "center",
    overflowWrap: "anywhere",
  },
  prizeAwardButton: {
    minHeight: 38,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.65)",
    color: "white",
    background: "linear-gradient(90deg, #ff087e, #8f2dff)",
    fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
    fontSize: 12,
    fontWeight: 1000,
    textTransform: "uppercase",
    letterSpacing: 0.8,
    cursor: "pointer",
  },
  prizeModalRow: {
    display: "grid",
    gridTemplateColumns: "112px 1fr",
    gap: 12,
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid rgba(255,255,255,0.14)",
    fontSize: 15,
    fontWeight: 900,
  },

  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 0,
    margin: "0 14px 12px",
    borderRadius: 12,
    border: "2px solid rgba(255,255,255,0.25)",
    background: "rgba(0,0,0,0.12)",
    overflow: "hidden",
  },
  statBox: {
    minWidth: 0,
    borderRight: "1px solid rgba(255,255,255,0.16)",
    background: "transparent",
    padding: "7px 4px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  statBoxLast: {
    borderRight: "none",
  },
  statIcon: {
    fontSize: 18,
    opacity: 0.95,
    lineHeight: 1,
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statMain: {
    fontWeight: 900,
    color: "white",
    fontSize: 13,
    lineHeight: "14px",
    whiteSpace: "nowrap",
  },
  statSub: {
    fontWeight: 800,
    color: "rgba(255,255,255,0.9)",
    fontSize: 8,
    lineHeight: "9px",
    letterSpacing: 0.55,
    whiteSpace: "nowrap",
  },
  cardActionsRow: {
    width: "calc(100% - 28px)",
    margin: "0 14px 12px",
    borderRadius: 14,
    border: "2px solid rgba(255,255,255,0.25)",
    background: "rgba(255,255,255,0.10)",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    overflow: "hidden",
  },
  cardActionButton: {
    minWidth: 0,
    minHeight: 50,
    border: "none",
    background: "transparent",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "0 9px",
    fontWeight: 1000,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    fontSize: 10,
    lineHeight: 1.08,
    cursor: "pointer",
    fontFamily: "inherit",
    WebkitTapHighlightColor: "transparent",
  },
  cardActionButtonRight: {
    borderLeft: "1px solid rgba(255,255,255,0.20)",
  },
  cardActionContent: {
    minWidth: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  cardActionIcon: {
    flexShrink: 0,
    fontSize: 12,
    color: "rgba(255,255,255,0.86)",
  },
  cardActionText: {
    minWidth: 0,
    whiteSpace: "normal",
  },
  cardActionChevron: {
    flexShrink: 0,
    fontSize: 10,
    color: "rgba(255,255,255,0.72)",
  },

  cta: {
    width: "100%",
    height: 54,
    border: "none",
    borderRadius: "0 0 16px 16px",
    overflow: "hidden",
    background:
      "linear-gradient(90deg, rgba(255,0,170,0.95) 0%, rgba(160,60,255,0.95) 100%)",
    color: "white",
    fontWeight: 1000,
    letterSpacing: 1,
    fontSize: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    cursor: "pointer",
  },
  ctaArrow: { fontSize: 22, marginTop: -1 },

	  successCard: {
	  width: "min(420px, 100%)",
	  margin: "calc(18px + env(safe-area-inset-top)) auto calc(110px + env(safe-area-inset-bottom))",
	  borderRadius: 24,
	  border: "2px solid rgba(255,255,255,0.28)",
	  background:
	    "linear-gradient(145deg, rgba(23,12,74,0.98) 0%, rgba(52,18,106,0.98) 100%)",
	  padding: "22px 20px",
	  fontFamily: "'Cooper Hewitt', system-ui, -apple-system, Arial, sans-serif",
	  boxShadow:
	    "0 26px 70px rgba(0,0,0,0.52), 0 0 34px rgba(255,0,170,0.22), inset 0 1px 0 rgba(255,255,255,0.16)",
	  boxSizing: "border-box",
	},

successTitle: {
  fontWeight: 1000,
  textTransform: "uppercase",
	  letterSpacing: 1,
	  fontSize: 18,
	  color: "white",
	  marginBottom: 18,
	  lineHeight: 1.25,
	  textAlign: "center",
	},

successRow: {
	  display: "flex",
	  alignItems: "center",
	  gap: 12,
	  marginBottom: 12,
	  borderRadius: 16,
	  border: "1px solid rgba(255,255,255,0.18)",
	  background: "rgba(255,255,255,0.08)",
	  padding: "12px 14px",
	},

successIcon: {
  fontSize: 24,
  width: 34,
  textAlign: "center",
},

successText: {
	  fontWeight: 900,
	  fontSize: 18,
	  color: "white",
	  textTransform: "uppercase",
	},

successInfoRow: {
	  display: "flex",
	  gap: 12,
	  alignItems: "flex-start",
	  marginBottom: 18,
	  borderRadius: 16,
	  border: "1px solid rgba(255,90,90,0.42)",
	  background: "rgba(255,59,48,0.16)",
	  padding: "12px 14px",
	},

successWarnIcon: {
	  fontSize: 22,
	  lineHeight: 1,
	  color: "#ffd7d7",
	},

successInfoText: {
  fontWeight: 900,
  textTransform: "uppercase",
	  letterSpacing: 0.6,
	  fontSize: 12,
	  lineHeight: 1.35,
	  color: "#ffd7d7",
	},

successBtnPrimary: {
	  width: "100%",
	  height: 56,
	  border: "2px solid rgba(255,255,255,0.55)",
	  marginTop: 8,
	  background:
	    "linear-gradient(135deg, #ff007f 0%, #7a2bff 100%)",
	  color: "white",
	  fontWeight: 1000,
	  textTransform: "uppercase",
	  letterSpacing: 1,
	  fontSize: 16,
	  cursor: "pointer",
	  borderRadius: 999,
	  boxShadow: "0 12px 28px rgba(255,0,170,0.24)",
	},

successBtnSecondary: {
	  width: "100%",
	  height: 56,
	  border: "2px solid rgba(255,255,255,0.34)",
	  marginTop: 10,
	  fontSize: 16,
	  background: "rgba(255,255,255,0.10)",
	  color: "white",
	  fontWeight: 1000,
	  textTransform: "uppercase",
	  letterSpacing: 1,
	  cursor: "pointer",
	  borderRadius: 999,
	},

successBtnDanger: {
	  width: "100%",
	  height: 56,
	  border: "2px solid rgba(255,255,255,0.34)",
	  marginTop: 8,
	  background: "rgba(255,59,48,0.82)",
	  color: "white",
	  fontWeight: 1000,
	  textTransform: "uppercase",
	  letterSpacing: 1,
	  cursor: "pointer",
	  borderRadius: 999,
	},

statusStarted: {
  border: "2px solid rgba(52, 152, 219, 0.95)",
  color: "rgba(52, 152, 219, 1)",
},

statusCannotStart: {
  border: "2px solid rgba(200, 200, 200, 0.75)",
  color: "rgba(220, 220, 220, 0.95)",
},

statusInvalid: {
  color: "rgba(0,0,0,1)",
  dotBorder: "1px solid rgba(255,255,255,0.58)",
  dotBoxShadow: "0 0 0 1px rgba(255,255,255,0.18), 0 0 10px rgba(255,255,255,0.18)",
},



 
};
